import React, { useState, useEffect, useRef } from 'react';
import { Wallet, Client, TrustSet, Payment, xrpToDrops, isValidClassicAddress } from 'xrpl';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import TrustlineManager from './components/TrustlineManager';
import EvidenceVault from './components/EvidenceVault';
import ArbitrageDashboard from './components/ArbitrageDashboard';
import MoneyFlowDashboard from './components/MoneyFlowDashboard';
import XamanServicesHub from './components/XamanServicesHub';
import { StellarDEXDashboard } from './components/StellarDEXDashboard';
import { createXummPayload, getXummPayload, type XummPayloadStatusResponse } from './xummApi';
import { getStorageItem, setStorageItem, removeStorageItem, isXamanContext } from './utils/xummStorage';
import { XummLink } from './components/XummLink';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import ProfessionalLanding from './ProfessionalLanding';

type PageKey =
  | 'landing'
  | 'services'
  | 'dex'
  | 'stellar'
  | 'amm'
  | 'tokens'
  | 'wallets'
  | 'portfolio'
  | 'pof'
  | 'pofaas'
  | 'legal'
  | 'terms'
  | 'privacy'
  | 'faq'
  | 'contact'
  | 'arbitrage'
  | 'ecosystem'
  | 'xaman-services'
  | 'member-benefits';

type IssuedAmount = { currency: string; issuer: string; value: string };
type XRPLAmount = string | IssuedAmount; // string is XRP in drops, object is issued currency
type BookOfferLike = { TakerGets: XRPLAmount; TakerPays: XRPLAmount };

type PaymentTxLike = {
  TransactionType: 'Payment';
  Amount?: unknown;
  date?: number;
};

type IssuedAmountLike = { value: string; currency?: string; issuer?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isIssuedAmountLike(value: unknown): value is IssuedAmountLike {
  if (!isRecord(value)) return false;
  return typeof value.value === 'string';
}

function isPaymentTxLike(value: unknown): value is PaymentTxLike {
  if (!isRecord(value)) return false;
  return value.TransactionType === 'Payment';
}

function amountToNumber(amount: XRPLAmount): number {
  // XRPL convention:
  // - XRP amounts are encoded as a string of drops
  // - Issued currencies are objects with a decimal `value`
  if (typeof amount === 'string') return Number(amount) / 1_000_000;
  return Number(amount.value);
}

function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function isLikelyInAppBrowser(): boolean {
  // Many in-app browsers block downloads and sometimes kill the webview on attempted file saves.
  // We treat these as "no-download" environments and prefer clipboard export.
  try {
    const ua = (navigator?.userAgent || '').toLowerCase();
    if (!ua) return false;
    return (
      ua.includes('fbav') ||
      ua.includes('fban') ||
      ua.includes('instagram') ||
      ua.includes('tiktok') ||
      ua.includes('snapchat') ||
      ua.includes('line') ||
      ua.includes('wechat') ||
      ua.includes('wv') // Android WebView marker
    );
  } catch {
    return false;
  }
}

function downloadJson(filename: string, data: unknown) {
  const payload = JSON.stringify(data, null, 2);

  // Xaman xApp: direct file downloads are not reliable and can cause the webview to close.
  // Best-effort fallback: copy JSON to clipboard so the user can paste/save it.
  if (isXamanContext() || isLikelyInAppBrowser()) {
    void (async () => {
      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(payload);
          alert(`Export copied to clipboard.\n\nPaste into Notes / a secure vault and save it as: ${filename}`);
          return;
        }
      } catch {
        // ignore and fall through to alert
      }

      // Clipboard unavailable: show the payload as a last resort.
      alert(`Export ready, but clipboard is unavailable.\n\nPlease manually copy the JSON shown next and save it as: ${filename}\n\n${payload}`);
    })();
    return;
  }

  const blob = new Blob([payload], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function utf8ToHex(input: string): string {
  const bytes = new TextEncoder().encode(input);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

function getAccountLinesResult(respUnknown: unknown): Record<string, unknown> | null {
  const respObj = (typeof respUnknown === 'object' && respUnknown !== null)
    ? (respUnknown as Record<string, unknown>)
    : null;
  const resultObj = (respObj && typeof respObj.result === 'object' && respObj.result !== null)
    ? (respObj.result as Record<string, unknown>)
    : null;
  return resultObj;
}

function hasTrustLine(linesResult: Record<string, unknown> | null, currency: string, issuer: string): boolean {
  if (!linesResult) return false;
  const lines = Array.isArray(linesResult.lines) ? (linesResult.lines as Array<unknown>) : [];
  const cur = currency.toUpperCase();
  return lines.some((lineUnknown) => {
    if (typeof lineUnknown !== 'object' || lineUnknown === null) return false;
    const line = lineUnknown as Record<string, unknown>;
    const lineCurrency = typeof line.currency === 'string' ? line.currency.toUpperCase() : '';
    const lineIssuer = typeof line.account === 'string' ? line.account : '';
    return lineCurrency === cur && lineIssuer === issuer;
  });
}

type CurrencyKey = 'XRP' | 'USDT' | 'USD' | 'RLUSD';

function isProbablyXrplClassicAddress(address: string): boolean {
  const a = address.trim();
  // Use xrpl.js checksum validation for safer UX.
  return isValidClassicAddress(a);
}

function toXrplAmount(currency: CurrencyKey, issuer: string, value: string): XRPLAmount {
  if (currency === 'XRP') {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) throw new Error('Invalid XRP amount');
    return xrpToDrops(n);
  }
  const v = String(value).trim();
  if (!v || Number(v) <= 0) throw new Error('Invalid issued-currency amount');
  return { currency, issuer, value: v };
}

function formatXrplAmount(amount: XRPLAmount): string {
  if (typeof amount === 'string') {
    const drops = Number(amount);
    const xrp = Number.isFinite(drops) ? drops / 1_000_000 : 0;
    return `${xrp.toFixed(6)} XRP`;
  }
  return `${amount.value} ${amount.currency} (${amount.issuer})`;
}

function applySlippage(amount: XRPLAmount, pct: number): XRPLAmount {
  const safePct = Number.isFinite(pct) ? pct : 0;
  const factor = 1 + Math.max(0, safePct) / 100;

  if (typeof amount === 'string') {
    const drops = Number(amount);
    if (!Number.isFinite(drops) || drops <= 0) throw new Error('Invalid XRP amount (drops)');
    return String(Math.ceil(drops * factor));
  }

  const v = Number(amount.value);
  if (!Number.isFinite(v) || v <= 0) throw new Error('Invalid issued amount');
  return {
    currency: amount.currency,
    issuer: amount.issuer,
    value: String(v * factor)
  };
}

const UnykornLanding: React.FC = () => {
  const [page, setPage] = useState<PageKey>('landing');
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mintWalletOpen, setMintWalletOpen] = useState(false);
  
  // Admin/Login state
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const loadAdminState = async () => {
      const stored = await getStorageItem('unykorn_admin_auth');
      setIsAdmin(stored === 'true');
    };
    loadAdminState();
  }, []);

  const inXaman = isXamanContext();

  // Track mint modal state without re-triggering scroll effects.
  const mintWalletOpenRef = useRef(false);
  useEffect(() => {
    mintWalletOpenRef.current = mintWalletOpen;
  }, [mintWalletOpen]);

  // Scroll to top when page changes
  useEffect(() => {
    // Smooth scrolling can cause viewport jumping in mobile browsers and xApp webviews.
    // Also: never force-scroll while a modal is open.
    if (mintWalletOpenRef.current) return;
    const id = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: isMobile ? 'auto' : 'auto' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [page, isMobile]);

  // Prevent background scroll / iOS rubber-banding when mint modal is open.
  useEffect(() => {
    if (!mintWalletOpen) return;

    const scrollY = window.scrollY || 0;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflowY: body.style.overflowY,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflowY = 'hidden';

    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflowY = prev.overflowY;
      window.scrollTo({ top: scrollY, behavior: 'auto' });
    };
  }, [mintWalletOpen]);
  const [mintedWallet, setMintedWallet] = useState<{address: string, seed: string} | null>(null);
  const [mintGenerating, setMintGenerating] = useState(false);

  // Mint UX guardrails
  const [mintPreflightAck, setMintPreflightAck] = useState(false);
  const [mintActivation, setMintActivation] = useState<{
    checking: boolean;
    exists: boolean | null;
    balanceXrp?: number;
    error?: string;
    checkedAt?: string;
  }>({ checking: false, exists: null });

  // Wallet backup guardrails: every generated wallet must be exported + acknowledged
  // (or explicitly discarded) before the UI will let you proceed.
  const [mintBackupDownloaded, setMintBackupDownloaded] = useState(false);
  const [mintBackupAck, setMintBackupAck] = useState(false);
  const [mintAckPhrase, setMintAckPhrase] = useState('');

  const [userWalletBackupDownloaded, setUserWalletBackupDownloaded] = useState(false);
  const [userWalletBackupAck, setUserWalletBackupAck] = useState(false);
  const [userWalletAckPhrase, setUserWalletAckPhrase] = useState('');

  const [clientWalletBackupDownloaded, setClientWalletBackupDownloaded] = useState(false);
  const [clientWalletBackupAck, setClientWalletBackupAck] = useState(false);
  const [clientWalletAckPhrase, setClientWalletAckPhrase] = useState('');

  const backupPhraseOk = (v: string) => v.trim().toUpperCase() === 'I SAVED IT';

  const downloadWalletBackup = (label: string, w: { address: string; seed: string }) => {
    // This file contains a private key/seed. Treat it like cash.
    const safeLabel = label.replace(/[^a-z0-9\-_. ]/gi, '').trim().replace(/\s+/g, '-');
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    downloadJson(`uny-wallet-backup-${safeLabel || 'wallet'}-${ts}.json`, {
      type: 'XRPL_WALLET_BACKUP',
      createdAt: new Date().toISOString(),
      network: 'XRPL Mainnet',
      app: 'UNYKORN',
      warning: 'This file contains an XRPL seed. Anyone with it controls the wallet. Store offline and never commit to git.',
      label,
      address: w.address,
      seed: w.seed
    });
  };

  // Backend audit log (public address only). This helps operations verify "mint" activity
  // without ever collecting seeds/private keys.
  const logMintEvent = async (event: string, address: string) => {
    try {
      if (!isValidClassicAddress(address)) return;
      await fetch('/api/mint/log', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          event,
          address,
          createdAt: new Date().toISOString()
        })
      });
    } catch {
      // Non-fatal: logging should never break mint flow.
    }
  };

  // Transaction safety: prevent accidental mainnet submissions until explicitly armed.
  const TX_ARM_STORAGE_KEY = 'unykorn.txArmedUntilMs.v1';
  const [txArmedUntilMs, setTxArmedUntilMs] = useState<number>(0);
  const [txNowMs, setTxNowMs] = useState<number>(() => Date.now());
  const [txArmPhrase, setTxArmPhrase] = useState('');

  // Load transaction arm status from storage on mount
  useEffect(() => {
    (async () => {
      try {
        const raw = await getStorageItem(TX_ARM_STORAGE_KEY);
        const n = raw ? Number(raw) : 0;
        setTxArmedUntilMs(Number.isFinite(n) ? n : 0);
      } catch {
        setTxArmedUntilMs(0);
      }
    })();
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => setTxNowMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const txArmed = txNowMs < txArmedUntilMs;
  const txRemainingSec = txArmed ? Math.max(0, Math.ceil((txArmedUntilMs - txNowMs) / 1000)) : 0;

  const armTxForMinutes = async (minutes: number) => {
    const mins = Number.isFinite(minutes) ? minutes : 5;
    const until = Date.now() + Math.max(1, mins) * 60_000;
    setTxArmedUntilMs(until);
    try {
      await setStorageItem(TX_ARM_STORAGE_KEY, String(until));
    } catch {
      // ignore
    }
    setTxArmPhrase('');
  };

  const disarmTx = async () => {
    setTxArmedUntilMs(0);
    try {
      await removeStorageItem(TX_ARM_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const ensureTxArmedOrAlert = (actionLabel: string): boolean => {
    if (txArmed) return true;
    alert(`Safe Mode is ON. This app will not submit mainnet transactions until you ARM sending.\n\nBlocked action: ${actionLabel}`);
    return false;
  };

  const buildStamp = 'UNYKORN • 2026-01-03';

  const TxSafetyPanel = () => (
    <div
      style={{
        padding: isMobile ? '18px' : '20px',
        background: txArmed
          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.16) 0%, rgba(15, 23, 42, 0.25) 100%)'
          : 'linear-gradient(135deg, rgba(239, 68, 68, 0.10) 0%, rgba(15, 23, 42, 0.25) 100%)',
        border: txArmed ? '1px solid rgba(16, 185, 129, 0.30)' : '1px solid rgba(239, 68, 68, 0.22)',
        borderRadius: '16px',
        marginBottom: '18px'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '12px', color: txArmed ? '#6ee7b7' : '#fecaca', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 900 }}>
            Transaction Safety
          </div>
          <div style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#ffffff', marginTop: '8px' }}>
            {txArmed ? `ARMED — sending enabled (${txRemainingSec}s)` : 'SAFE MODE — sending disabled'}
          </div>
          <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px', lineHeight: 1.6, maxWidth: '980px' }}>
            This prevents accidental mainnet submissions. To enable sending, type <strong style={{ color: '#e5e7eb' }}>ARM</strong> and arm for a short window.
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {!txArmed && (
            <input
              value={txArmPhrase}
              onChange={(e) => setTxArmPhrase(e.target.value)}
              placeholder="Type ARM"
              style={{
                width: isMobile ? '100%' : '180px',
                padding: '12px 14px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '10px',
                color: '#ffffff',
                fontSize: '14px',
                fontFamily: 'monospace'
              }}
            />
          )}

          <button
            onClick={() => {
              if (txArmed) {
                disarmTx();
                return;
              }
              if (txArmPhrase.trim().toUpperCase() !== 'ARM') {
                alert('Type ARM to enable sending.');
                return;
              }
              armTxForMinutes(5);
            }}
            style={{
              padding: '12px 14px',
              fontSize: '12px',
              fontWeight: 900,
              background: txArmed ? 'rgba(239, 68, 68, 0.14)' : '#ffffff',
              border: txArmed ? '1px solid rgba(239, 68, 68, 0.25)' : 'none',
              borderRadius: '10px',
              color: txArmed ? '#fecaca' : '#000000',
              cursor: 'pointer',
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }}
          >
            {txArmed ? 'Disarm' : 'Arm 5 min'}
          </button>
        </div>
      </div>
    </div>
  );

  const navItems: Array<{ key: PageKey; label: string; labelUpper: string; icon?: string }> = [
    { key: 'landing', label: 'Home', labelUpper: 'HOME', icon: '🏠' },
    { key: 'pof', label: 'Verification', labelUpper: 'VERIFICATION', icon: '✓' },
    { key: 'dex', label: 'Settlement', labelUpper: 'SETTLEMENT', icon: '⚡' },
    { key: 'stellar', label: 'Stellar DEX', labelUpper: 'STELLAR DEX', icon: '🌟' },
    { key: 'xaman-services', label: 'Xaman Services', labelUpper: 'XAMAN SERVICES', icon: '🛒' },
    { key: 'arbitrage', label: 'Arbitrage', labelUpper: 'ARBITRAGE', icon: '📈' },
    { key: 'ecosystem', label: 'Live Ecosystem', labelUpper: 'LIVE ECOSYSTEM', icon: '🌐' },
    { key: 'amm', label: 'Liquidity', labelUpper: 'LIQUIDITY', icon: '💧' },
    { key: 'tokens', label: 'Issuance', labelUpper: 'ISSUANCE', icon: '🪙' },
    { key: 'member-benefits', label: 'Member Benefits', labelUpper: 'MEMBER BENEFITS', icon: '⭐' },
  ];

  // Floating Home Button Component - appears on all non-home pages
  const FloatingHomeButton = () => {
    if (page === 'landing') return null; // Don't show on home page
    
    return (
      <button
        onClick={() => {
          setPage('landing');
          setMobileMenuOpen(false);
        }}
        style={{
          position: 'fixed',
          bottom: isMobile ? '20px' : '30px',
          right: isMobile ? '20px' : '30px',
          width: isMobile ? '56px' : '64px',
          height: isMobile ? '56px' : '64px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          border: '2px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          transition: 'all 0.3s ease',
          color: '#ffffff',
          fontSize: isMobile ? '24px' : '28px',
          fontWeight: 'bold',
          WebkitTapHighlightColor: 'transparent'
        }}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 12px 48px rgba(59, 130, 246, 0.6)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.4)';
          }
        }}
        onTouchStart={(e) => {
          e.currentTarget.style.transform = 'scale(0.95)';
        }}
        onTouchEnd={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title="Back to Home"
        aria-label="Back to Home"
      >
        <span style={{ marginTop: '-2px' }}>🏠</span>
      </button>
    );
  };

  const serviceCards: Array<{ title: string; page: PageKey; desc: string; price: string }> = [
    {
      title: 'Verification',
      page: 'pof',
      desc: 'Institutional proof of funds & trust line validation. Balances are observable on-ledger and vary over time.',
      price: 'Pricing on request'
    },
    {
      title: 'POF-as-a-Service',
      page: 'pofaas',
      desc: 'Guided wallet setup and trust line configuration for on-ledger proof-of-funds workflows (non-custodial).',
      price: 'Custom quote'
    },
    {
      title: 'Settlement',
      page: 'dex',
      desc: 'Settlement tooling on XRPL (DEX + path payments). Access is gated and availability varies.',
      price: 'Request access'
    },
    {
      title: 'Liquidity',
      page: 'amm',
      desc: 'AMM and liquidity tooling (experimental). No yield or return guarantees.',
      price: 'Varies'
    },
    {
      title: 'Issuance',
      page: 'tokens',
      desc: 'Create & manage XRPL issued assets (IOUs). Full issuer wallet setup.',
      price: 'Custom quote'
    }
  ];
  
  // Authentication State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userWallet, setUserWallet] = useState<{address: string, seed: string} | null>(null);
  const [loginStep, setLoginStep] = useState<'login' | 'register' | 'authenticated'>('login');

  // Xaman (XUMM) signing session (no seeds stored in browser)
  const XUMM_USE_KEY = 'unykorn.useXumm.v1';
  const XUMM_ACCOUNT_KEY = 'unykorn.xumm.account.v1';

  const [useXumm, setUseXumm] = useState<boolean>(false);
  const [xummAccount, setXummAccount] = useState<string>('');

  // Load Xaman preferences from storage on mount
  useEffect(() => {
    (async () => {
      try {
        const useValue = await getStorageItem(XUMM_USE_KEY);
        setUseXumm(useValue === '1');
        
        const account = await getStorageItem(XUMM_ACCOUNT_KEY);
        setXummAccount(account || '');
      } catch {
        // ignore
      }
    })();
  }, []);

  // Xaman payload lookup (UUID -> status/txid). Useful for reconciling Xaman dashboard entries.
  const [payloadLookupUuid, setPayloadLookupUuid] = useState('');
  const [payloadLookupBusy, setPayloadLookupBusy] = useState(false);
  const [payloadLookupStatus, setPayloadLookupStatus] = useState<XummPayloadStatusResponse | null>(null);
  const [payloadLookupError, setPayloadLookupError] = useState<string>('');

  type XummFlowState = {
    open: boolean;
    label: string;
    uuid: string;
    qrPng?: string;
    next?: string;
    status?: XummPayloadStatusResponse;
    error?: string;
    done?: boolean;
  };

  const [xummFlow, setXummFlow] = useState<XummFlowState | null>(null);

  const persistUseXumm = async (next: boolean) => {
    setUseXumm(next);
    try {
      await setStorageItem(XUMM_USE_KEY, next ? '1' : '0');
    } catch {
      // ignore
    }
  };

  const handleAdminLogin = async () => {
    const password = prompt('Enter admin password:');
    if (password === 'unykorn2026') {
      setIsAdmin(true);
      try {
        await setStorageItem('unykorn_admin_auth', 'true');
      } catch {
        // ignore
      }
    } else if (password !== null) {
      alert('Invalid password');
    }
  };

  const handleAdminLogout = async () => {
    setIsAdmin(false);
    try {
      await removeStorageItem('unykorn_admin_auth');
    } catch {
      // ignore
    }
  };

  const persistXummAccount = async (account: string) => {
    const a = account.trim();
    setXummAccount(a);
    try {
      if (a) await setStorageItem(XUMM_ACCOUNT_KEY, a);
      else await removeStorageItem(XUMM_ACCOUNT_KEY);
    } catch {
      // ignore
    }
  };

  const lookupXummPayload = async (uuidRaw: string) => {
    const uuid = uuidRaw.trim();
    if (!uuid) {
      setPayloadLookupError('Paste a payload UUID first.');
      setPayloadLookupStatus(null);
      return;
    }

    setPayloadLookupBusy(true);
    setPayloadLookupError('');
    setPayloadLookupStatus(null);
    try {
      const status = await getXummPayload(uuid);
      setPayloadLookupStatus(status);
    } catch (error) {
      setPayloadLookupError(formatErrorMessage(error));
    } finally {
      setPayloadLookupBusy(false);
    }
  };

  const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

  // Polling method for payload status (fallback for browser context)
  const waitForXummResolution = async (uuid: string, timeoutMs = 180_000): Promise<XummPayloadStatusResponse> => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const status = await getXummPayload(uuid);
      setXummFlow((prev) => prev && prev.uuid === uuid ? { ...prev, status } : prev);

      const meta = status.meta || {};
      if (meta.resolved || meta.cancelled || meta.expired) return status;
      await sleep(1500);
    }
    throw new Error('Xaman signing timed out.');
  };

  const runXummFlow = async (txjson: Record<string, unknown>, label: string): Promise<XummPayloadStatusResponse> => {
    const payload = await createXummPayload(txjson, {
      custom_meta: {
        app: 'UNYKORN',
        label
      }
    });

    setXummFlow({
      open: true,
      label,
      uuid: payload.uuid,
      qrPng: payload.refs?.qr_png,
      next: payload.next?.always
    });

    try {
      const status = await waitForXummResolution(payload.uuid);
      setXummFlow((prev) => prev && prev.uuid === payload.uuid ? { ...prev, status, done: true } : prev);
      return status;
    } catch (error) {
      const message = formatErrorMessage(error);
      setXummFlow((prev) => prev && prev.uuid === payload.uuid ? { ...prev, error: message, done: true } : prev);
      throw error;
    }
  };

  const waitForValidatedTx = async (client: Client, txid: string, timeoutMs = 120_000): Promise<Record<string, unknown> | null> => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      try {
        const respUnknown: unknown = await client.request({ command: 'tx', transaction: txid } as unknown as never);
        const respObj = (typeof respUnknown === 'object' && respUnknown !== null)
          ? (respUnknown as Record<string, unknown>)
          : null;
        const resultObj = (respObj && typeof respObj.result === 'object' && respObj.result !== null)
          ? (respObj.result as Record<string, unknown>)
          : null;

        if (resultObj && resultObj.validated === true) {
          return resultObj;
        }
      } catch {
        // keep polling
      }

      await sleep(2000);
    }
    return null;
  };

  const xummSubmit = async (preparedTx: Record<string, unknown>, label: string): Promise<{ txid?: string; status?: unknown }> => {
    const status = await runXummFlow(preparedTx, label);
    const meta = status.meta || {};
    if (meta.cancelled) throw new Error('Xaman signing was cancelled.');
    if (meta.expired) throw new Error('Xaman payload expired.');
    if (!meta.signed) throw new Error('Xaman payload not signed.');
    const txid = typeof status.response?.txid === 'string' ? status.response?.txid : undefined;
    return { txid, status };
  };

  const XummModal = () => {
    if (!xummFlow?.open) return null;
    const meta = xummFlow.status?.meta || {};
    const signed = meta.signed === true;
    const resolved = meta.resolved === true;
    const cancelled = meta.cancelled === true;
    const expired = meta.expired === true;

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <div style={{
          width: 'min(560px, 96vw)',
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(0, 0, 0, 0.92) 100%)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '16px',
          padding: '18px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#a7f3d0', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 900 }}>
                Xaman (XUMM)
              </div>
              <div style={{ fontSize: '18px', color: '#ffffff', fontWeight: 800, marginTop: '6px' }}>
                {xummFlow.label}
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '6px', lineHeight: 1.6 }}>
                Scan the QR or open the deep link in Xaman to review, sign, and submit.
              </div>
            </div>

            <button
              onClick={() => setXummFlow(null)}
              style={{
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '10px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}
            >
              Close
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px', marginTop: '14px' }}>
            <div style={{
              padding: '14px',
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '14px'
            }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>QR</div>
              {xummFlow.qrPng ? (
                <img src={xummFlow.qrPng} alt="Xaman QR" style={{ width: '100%', borderRadius: '10px' }} />
              ) : (
                <div style={{ fontSize: '13px', color: '#9ca3af' }}>QR not available.</div>
              )}
            </div>

            <div style={{
              padding: '14px',
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '14px'
            }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px' }}>Status</div>

              <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: 1.7 }}>
                <div><strong style={{ color: '#93c5fd' }}>UUID:</strong> <span style={{ fontFamily: 'monospace' }}>{xummFlow.uuid}</span></div>
                <div><strong style={{ color: '#93c5fd' }}>Signed:</strong> {signed ? 'YES' : 'NO'}</div>
                <div><strong style={{ color: '#93c5fd' }}>Resolved:</strong> {resolved ? 'YES' : 'NO'}</div>
                <div><strong style={{ color: '#93c5fd' }}>Cancelled:</strong> {cancelled ? 'YES' : 'NO'}</div>
                <div><strong style={{ color: '#93c5fd' }}>Expired:</strong> {expired ? 'YES' : 'NO'}</div>
              </div>

              {xummFlow.next && (
                <XummLink
                  href={xummFlow.next}
                  style={{
                    display: 'inline-block',
                    marginTop: '12px',
                    padding: '12px 14px',
                    background: '#ffffff',
                    borderRadius: '10px',
                    color: '#000000',
                    textDecoration: 'none',
                    fontSize: '12px',
                    fontWeight: 900,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  Open in Xaman
                </XummLink>
              )}

              {xummFlow.error && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: 'rgba(239,68,68,0.12)',
                  border: '1px solid rgba(239,68,68,0.22)',
                  borderRadius: '12px',
                  color: '#fecaca',
                  fontSize: '13px'
                }}>
                  {xummFlow.error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Live Pricing State
  const [xrpPrice, setXrpPrice] = useState<number>(0);
  const [rlusdPrice] = useState<number>(1.00);
  
  // DEX Live Data State
  const [orderBook, setOrderBook] = useState<{bids: Array<{price: string, amount: string}>, asks: Array<{price: string, amount: string}>}>({bids: [], asks: []});
  const [priceHistory, setPriceHistory] = useState<Array<{time: string, price: number}>>([]);
  const [recentTrades, setRecentTrades] = useState<Array<{time: string, price: string, amount: string, type: 'buy' | 'sell'}>>([]);
  const [isLoadingDex, setIsLoadingDex] = useState(false);

  // Instant Settlement (XRPL Payment) State
  const [settlementDestination, setSettlementDestination] = useState('');
  const [settlementAmountXrp, setSettlementAmountXrp] = useState('');
  const [settlementDestinationTag, setSettlementDestinationTag] = useState('');
  const [settlementMemo, setSettlementMemo] = useState('');
  const [settlementSubmitting, setSettlementSubmitting] = useState(false);
  const [settlementPreparedTx, setSettlementPreparedTx] = useState<Record<string, unknown> | null>(null);
  const [settlementReceipt, setSettlementReceipt] = useState<{
    hash: string;
    validated: boolean;
    ledgerIndex?: number;
    transactionResult?: string;
  } | null>(null);

  // Issued Asset Settlement (IOU Payment) State
  const [iouDestination, setIouDestination] = useState('');
  const [iouAmount, setIouAmount] = useState('');
  const [iouCurrency, setIouCurrency] = useState<'USDT' | 'USD'>('USDT');
  const [iouIssuer, setIouIssuer] = useState('rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm');
  const [iouDestinationTag, setIouDestinationTag] = useState('');
  const [iouMemo, setIouMemo] = useState('');
  const [iouChecking, setIouChecking] = useState(false);
  const [iouSenderLines, setIouSenderLines] = useState<Record<string, unknown> | null>(null);
  const [iouDestLines, setIouDestLines] = useState<Record<string, unknown> | null>(null);
  const [iouPreflight, setIouPreflight] = useState<{ senderHasLine: boolean; destHasLine: boolean; destExists: boolean } | null>(null);
  const [iouSubmitting, setIouSubmitting] = useState(false);
  const [iouPreparedTx, setIouPreparedTx] = useState<Record<string, unknown> | null>(null);
  const [iouReceipt, setIouReceipt] = useState<{
    hash: string;
    validated: boolean;
    ledgerIndex?: number;
    transactionResult?: string;
  } | null>(null);

  // Path Payment (routed settlement) State
  const [pathDest, setPathDest] = useState('');
  const [pathDestTag, setPathDestTag] = useState('');
  const [pathMemo, setPathMemo] = useState('');
  const [pathDeliverCurrency, setPathDeliverCurrency] = useState<CurrencyKey>('USDT');
  const [pathDeliverIssuer, setPathDeliverIssuer] = useState('rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm');
  const [pathDeliverAmount, setPathDeliverAmount] = useState('');
  const [pathDeliverMin, setPathDeliverMin] = useState('');
  const [pathSendCurrency, setPathSendCurrency] = useState<CurrencyKey>('XRP');
  const [pathSendIssuer, setPathSendIssuer] = useState('rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm');
  const [pathSendMax, setPathSendMax] = useState('');
  const [pathSlippagePct, setPathSlippagePct] = useState('1.0');

  const [pathPreflightChecking, setPathPreflightChecking] = useState(false);
  const [pathDestAccountInfo, setPathDestAccountInfo] = useState<Record<string, unknown> | null>(null);
  const [pathSenderLines, setPathSenderLines] = useState<Record<string, unknown> | null>(null);
  const [pathDestLines, setPathDestLines] = useState<Record<string, unknown> | null>(null);
  const [pathPreflight, setPathPreflight] = useState<{
    destExists: boolean;
    destRequiresTag: boolean;
    destDepositAuth: boolean;
    senderHasSendLine: boolean;
    destHasDeliverLine: boolean;
  } | null>(null);

  const [pathFinding, setPathFinding] = useState(false);
  const [pathQuoteRequest, setPathQuoteRequest] = useState<Record<string, unknown> | null>(null);
  const [pathQuoteResult, setPathQuoteResult] = useState<Record<string, unknown> | null>(null);
  const [pathAlternatives, setPathAlternatives] = useState<Array<Record<string, unknown>>>([]);
  const [pathSelectedAltIndex, setPathSelectedAltIndex] = useState(0);

  const [pathSubmitting, setPathSubmitting] = useState(false);
  const [pathPreparedTx, setPathPreparedTx] = useState<Record<string, unknown> | null>(null);
  const [pathSubmitResult, setPathSubmitResult] = useState<Record<string, unknown> | null>(null);
  const [pathReceipt, setPathReceipt] = useState<{
    hash: string;
    validated: boolean;
    ledgerIndex?: number;
    transactionResult?: string;
    deliveredAmount?: XRPLAmount;
  } | null>(null);
  
  // POF Flow State
  const [pofStep, setPofStep] = useState(1);
  const [verificationType, setVerificationType] = useState('');
  const [verificationInput, setVerificationInput] = useState('');
  
  // POF-as-a-Service State
  const [clientWallet, setClientWallet] = useState<{address: string, seed: string} | null>(null);
  const [requestedAmount, setRequestedAmount] = useState('');
  const [generating, setGenerating] = useState(false);
  const [trustSetTxJson, setTrustSetTxJson] = useState<Record<string, unknown> | null>(null);
  const [trustSetSubmitting, setTrustSetSubmitting] = useState(false);
  const [trustSetReceipt, setTrustSetReceipt] = useState<{
    hash: string;
    validated: boolean;
    ledgerIndex?: number;
    transactionResult?: string;
  } | null>(null);
  const [trustLineSnapshot, setTrustLineSnapshot] = useState<Record<string, unknown> | null>(null);
  const [trustLineChecking, setTrustLineChecking] = useState(false);

  function MintWalletModal() {
    if (!mintWalletOpen) return null;

    const mintedAddressValid = mintedWallet ? isValidClassicAddress(mintedWallet.address) : false;

    const resetMintActivation = () => {
      setMintActivation({ checking: false, exists: null });
    };

    const checkMintActivation = async () => {
      if (!mintedWallet || !isValidClassicAddress(mintedWallet.address)) return;
      setMintActivation({ checking: true, exists: null });
      try {
        const c = new Client('wss://s1.ripple.com');
        await c.connect();
        try {
          const info = await c.request({
            command: 'account_info',
            account: mintedWallet.address,
            ledger_index: 'validated'
          });
          const balDrops = (info.result as unknown as { account_data?: { Balance?: string } }).account_data?.Balance;
          const balXrp = typeof balDrops === 'string' ? Number(balDrops) / 1_000_000 : 0;
          setMintActivation({
            checking: false,
            exists: true,
            balanceXrp: balXrp,
            checkedAt: new Date().toISOString()
          });
        } catch (e) {
          const msg = formatErrorMessage(e);
          const isNotFound = msg.toLowerCase().includes('actnotfound');
          setMintActivation({
            checking: false,
            exists: isNotFound ? false : null,
            error: msg,
            checkedAt: new Date().toISOString()
          });
        } finally {
          await c.disconnect();
        }
      } catch (e) {
        setMintActivation({ checking: false, exists: null, error: formatErrorMessage(e), checkedAt: new Date().toISOString() });
      }
    };

    const overlayStyle: React.CSSProperties = {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(8px)',
      zIndex: 2000,
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      paddingTop: isMobile ? '18px' : '24px',
      paddingLeft: isMobile ? '18px' : '24px',
      paddingRight: isMobile ? '18px' : '24px',
      paddingBottom: isMobile ? 'calc(18px + env(safe-area-inset-bottom))' : '24px',
      // Critical: keep the overlay itself non-scrollable.
      // On iOS/webviews, having BOTH overlay + card scrollable causes "jumping" as scroll containers compete.
      overflowY: 'hidden',
      overflowX: 'hidden',
      // Modern viewport unit helps reduce address-bar jumpiness on mobile browsers.
      height: '100dvh',
      // Prevent body scroll bounce on iOS
      touchAction: 'none',
      overscrollBehaviorY: 'contain'
    };

    const cardStyle: React.CSSProperties = {
      width: '100%',
      maxWidth: '820px',
      maxHeight: isMobile ? 'calc(100dvh - 40px - env(safe-area-inset-bottom))' : '85vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      background: 'linear-gradient(180deg, rgba(17, 24, 39, 0.92) 0%, rgba(0, 0, 0, 0.92) 100%)',
      border: '1px solid rgba(59, 130, 246, 0.25)',
      borderRadius: '16px',
      padding: isMobile ? '18px' : '24px',
      color: '#e5e7eb',
      margin: isMobile ? '0 auto' : 'auto',
      marginTop: isMobile ? '0' : 'auto',
      marginBottom: isMobile ? '0' : 'auto',
      WebkitOverflowScrolling: 'touch',
      // Prevent horizontal scroll/bounce
      touchAction: 'pan-y',
      overscrollBehaviorY: 'contain'
    };

    const primaryBtn: React.CSSProperties = {
      padding: '12px 16px',
      fontSize: '13px',
      fontWeight: '700',
      background: '#ffffff',
      border: 'none',
      borderRadius: '10px',
      color: '#000000',
      cursor: 'pointer',
      letterSpacing: '0.06em',
      textTransform: 'uppercase'
    };

    const secondaryBtn: React.CSSProperties = {
      padding: '12px 16px',
      fontSize: '13px',
      fontWeight: '700',
      background: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      borderRadius: '10px',
      color: '#ffffff',
      cursor: 'pointer',
      letterSpacing: '0.06em',
      textTransform: 'uppercase'
    };

    const copy = async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        alert('Copied to clipboard.');
      } catch {
        alert('Copy failed. Select the text and copy manually.');
      }
    };

    return (
      <div
        style={overlayStyle}
        onTouchMove={(e) => {
          // If the user is swiping on the backdrop (not the card), prevent iOS rubber-banding.
          if (e.target === e.currentTarget) e.preventDefault();
        }}
        onClick={(e) => {
          if (e.target !== e.currentTarget) return;

          // Guardrail: if a wallet was generated but not backed up, don't allow silent close.
          if (mintedWallet && !(mintBackupDownloaded && mintBackupAck)) {
            const discard = window.confirm(
              'This wallet has NOT been confirmed as backed up.\n\nTo prevent loss of funds, the app will not close until you either:\n- Download the backup and type: I SAVED IT\n\nOr explicitly DISCARD this wallet (you will lose access).\n\nDiscard this wallet now?'
            );
            if (discard) {
              setMintedWallet(null);
              setMintBackupDownloaded(false);
              setMintBackupAck(false);
              setMintAckPhrase('');
              setMintWalletOpen(false);
            }
            return;
          }

          setMintWalletOpen(false);
        }}
      >
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 800 }}>
                XRPL Protocol
              </div>
              <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 300, color: '#ffffff', marginTop: '10px' }}>
                Mint Wallet
              </div>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.6 }}>
                This generates a non-custodial XRPL wallet in your browser. Anyone with the seed can control the wallet.
              </div>
            </div>

            <button
              onClick={() => {
                if (mintedWallet && !(mintBackupDownloaded && mintBackupAck)) {
                  const discard = window.confirm(
                    'This wallet has NOT been confirmed as backed up.\n\nDiscard this wallet and close?'
                  );
                  if (!discard) return;
                  setMintedWallet(null);
                  setMintBackupDownloaded(false);
                  setMintBackupAck(false);
                  setMintAckPhrase('');
                }
                setMintWalletOpen(false);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                fontSize: '22px',
                cursor: 'pointer',
                padding: '6px 8px',
                lineHeight: 1
              }}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
          </div>

          <div style={{
            marginTop: '16px',
            padding: '14px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            borderRadius: '12px',
            fontSize: '13px',
            color: '#fecaca',
            lineHeight: 1.6
          }}>
            <strong style={{ color: '#fca5a5' }}>Safety note:</strong> Don’t mint or store high-value keys on a shared computer. Prefer a hardware wallet for serious funds.
          </div>

          <div style={{
            marginTop: '12px',
            padding: '14px',
            background: 'rgba(59, 130, 246, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.18)',
            borderRadius: '12px',
            fontSize: '13px',
            color: '#e5e7eb',
            lineHeight: 1.6
          }}>
            <div style={{ fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '12px', color: '#93c5fd' }}>
              Before you mint: mainnet walkthrough
            </div>
            <div style={{ marginTop: '10px', color: '#cbd5e1' }}>
              Minting creates a <strong style={{ color: '#ffffff' }}>valid address + seed</strong> locally in your browser.
              The address will <strong style={{ color: '#ffffff' }}>NOT</strong> exist on XRPL mainnet until it receives <strong style={{ color: '#ffffff' }}>XRP</strong> (activation / reserve).
            </div>
            <ol style={{ marginTop: '10px', paddingLeft: '18px', color: '#cbd5e1' }}>
              <li><strong>Mint</strong> a wallet and save the backup offline (seed = full control).</li>
              <li><strong>Activate</strong> the address by sending ~<strong>2 XRP</strong> to it (covers reserve + fees).</li>
              <li><strong>Only after activation:</strong> set required trustlines to receive issued tokens like <strong>USDT</strong>.</li>
              <li>Verify activation in Explorer: if you see <code>actNotFound</code>, it simply means “not activated yet”.</li>
            </ol>
            <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={mintPreflightAck}
                onChange={(e) => setMintPreflightAck(e.target.checked)}
                style={{ marginTop: '3px' }}
              />
              <span style={{ color: '#e5e7eb', fontSize: '13px' }}>
                I understand: this will generate a non-custodial seed, and the account won’t show on-ledger until I fund it with XRP.
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '16px' }}>
            <button
              onClick={() => {
                setMintedWallet(null);
                setMintBackupDownloaded(false);
                setMintBackupAck(false);
                setMintAckPhrase('');
                setMintGenerating(true);
                try {
                  const w = Wallet.generate();
                  const next = { address: w.address, seed: w.seed || '' };

                  // Sanity check: minted addresses must always be checksum-valid.
                  if (!isValidClassicAddress(next.address)) {
                    alert('Mint failed: generated address is not a valid XRPL classic address. Please refresh and try again.');
                    return;
                  }

                  setMintedWallet(next);

                  // Reset activation status for the newly minted wallet.
                  resetMintActivation();

                  // Audit log: record the public address mint event (no seed).
                  void logMintEvent('mint_wallet', next.address);

                  // Guardrail: auto-export a wallet backup file immediately.
                  downloadWalletBackup('Minted Wallet', next);
                  setMintBackupDownloaded(true);
                } catch (e) {
                  console.error('[Mint] Wallet generation failed:', e);
                  setMintedWallet(null);
                  setMintBackupDownloaded(false);
                  setMintBackupAck(false);
                  setMintAckPhrase('');
                  alert(
                    `Mint failed on this device/browser.\n\n${formatErrorMessage(e)}\n\nTry: update your browser, disable in-app browsers, or use Chrome/Safari directly.`
                  );
                } finally {
                  setMintGenerating(false);
                }
              }}
              style={{
                ...primaryBtn,
                opacity: mintGenerating ? 0.7 : 1,
                cursor: mintGenerating ? 'not-allowed' : 'pointer'
              }}
              disabled={mintGenerating || !mintPreflightAck}
            >
              {mintGenerating ? 'Generating…' : 'Generate Wallet'}
            </button>

            {mintedWallet && (
              <button
                onClick={() => {
                  if (!(mintBackupDownloaded && mintBackupAck)) {
                    alert('Backup required: download the backup and type “I SAVED IT” before using this wallet.');
                    return;
                  }
                  if (!isValidClassicAddress(mintedWallet.address)) {
                    alert('This wallet address is invalid. Please mint a new wallet.');
                    return;
                  }
                  void logMintEvent('use_for_pofaas', mintedWallet.address);
                  setClientWallet(mintedWallet);
                  setClientWalletBackupDownloaded(true);
                  setClientWalletBackupAck(true);
                  setClientWalletAckPhrase('I SAVED IT');
                  setPage('pofaas');
                  setMobileMenuOpen(false);
                  setMintWalletOpen(false);
                }}
                style={{
                  ...secondaryBtn,
                  opacity: (mintBackupDownloaded && mintBackupAck) ? 1 : 0.6,
                  cursor: (mintBackupDownloaded && mintBackupAck) ? 'pointer' : 'not-allowed'
                }}
              >
                Use for POFaaS
              </button>
            )}

            <button
              onClick={() => {
                setMintedWallet(null);
                setMintWalletOpen(false);
              }}
              style={secondaryBtn}
            >
              Close
            </button>
          </div>

          {mintedWallet && (
            <div style={{ marginTop: '18px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div style={{
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Address
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#e5e7eb', wordBreak: 'break-all' }}>
                  {mintedWallet.address}
                </div>
                {!mintedAddressValid && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#fca5a5', fontWeight: 800 }}>
                    Warning: address failed checksum validation. Do not fund. Mint again.
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => copy(mintedWallet.address)}
                    style={{ ...secondaryBtn, opacity: mintedAddressValid ? 1 : 0.5, cursor: mintedAddressValid ? 'pointer' : 'not-allowed' }}
                    disabled={!mintedAddressValid}
                  >
                    Copy Address
                  </button>
                  <XummLink
                    href={`https://livenet.xrpl.org/accounts/${mintedWallet.address}`}
                    style={{
                      ...secondaryBtn,
                      display: 'inline-flex',
                      alignItems: 'center',
                      textDecoration: 'none',
                      opacity: mintedAddressValid ? 1 : 0.5,
                      pointerEvents: mintedAddressValid ? 'auto' : 'none'
                    }}
                  >
                    View Explorer
                  </XummLink>

                  <button
                    onClick={() => void checkMintActivation()}
                    style={{
                      ...secondaryBtn,
                      opacity: mintedAddressValid ? 1 : 0.5,
                      cursor: mintedAddressValid ? 'pointer' : 'not-allowed'
                    }}
                    disabled={!mintedAddressValid || mintActivation.checking}
                    title="Checks whether the account exists on-ledger yet (activated)"
                  >
                    {mintActivation.checking ? 'Checking…' : 'Check Activation'}
                  </button>
                </div>

                {mintedAddressValid && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#cbd5e1', lineHeight: 1.6 }}>
                    {mintActivation.exists === null && !mintActivation.error && (
                      <span>Activation status: <strong style={{ color: '#e5e7eb' }}>not checked</strong></span>
                    )}
                    {mintActivation.exists === false && (
                      <span>
                        Activation status: <strong style={{ color: '#fbbf24' }}>NOT ACTIVATED</strong> (expected until you send XRP)
                      </span>
                    )}
                    {mintActivation.exists === true && (
                      <span>
                        Activation status: <strong style={{ color: '#86efac' }}>ACTIVATED</strong>
                        {typeof mintActivation.balanceXrp === 'number' ? ` • Balance: ${mintActivation.balanceXrp.toFixed(6)} XRP` : ''}
                      </span>
                    )}
                    {mintActivation.error && (
                      <div style={{ marginTop: '6px', color: '#fca5a5' }}>
                        Activation check error: {mintActivation.error}
                      </div>
                    )}
                    {mintActivation.checkedAt && (
                      <div style={{ marginTop: '6px', color: '#9ca3af' }}>
                        Last checked: {new Date(mintActivation.checkedAt).toUTCString()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{
                padding: '14px',
                background: 'rgba(251, 191, 36, 0.08)',
                border: '1px solid rgba(251, 191, 36, 0.25)',
                borderRadius: '12px',
                maxHeight: isMobile ? '50vh' : '60vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollBehavior: 'smooth',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y'
              }}>
                <div style={{ fontSize: '12px', color: '#fbbf24', marginBottom: '8px' }}>
                  Private Seed (store securely)
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#fde68a', wordBreak: 'break-all' }}>
                  {mintedWallet.seed}
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <button onClick={() => copy(mintedWallet.seed)} style={secondaryBtn}>Copy Seed</button>
                  <button
                    onClick={() => {
                      downloadWalletBackup('Minted Wallet', mintedWallet);
                      setMintBackupDownloaded(true);
                    }}
                    style={secondaryBtn}
                  >
                    Download Backup
                  </button>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                    Type <strong style={{ color: '#e5e7eb' }}>I SAVED IT</strong> to confirm you exported the seed.
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={mintAckPhrase}
                      onChange={(e) => setMintAckPhrase(e.target.value)}
                      placeholder="I SAVED IT"
                      autoComplete="off"
                      autoFocus={!isMobile && !inXaman}
                      style={{
                        flex: 1,
                        minWidth: isMobile ? '100%' : '220px',
                        padding: '12px 14px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.border = '1px solid rgba(147, 197, 253, 0.5)'}
                      onBlur={(e) => e.target.style.border = '1px solid rgba(255, 255, 255, 0.12)'}
                    />
                    <button
                      onClick={() => {
                        if (!backupPhraseOk(mintAckPhrase)) {
                          alert('Please type: I SAVED IT');
                          return;
                        }
                        if (!mintBackupDownloaded) {
                          alert('Download the backup first (or download again), then confirm.');
                          return;
                        }
                        setMintBackupAck(true);
                      }}
                      style={{
                        ...secondaryBtn,
                        opacity: (mintBackupDownloaded && backupPhraseOk(mintAckPhrase)) ? 1 : 0.6,
                        cursor: (mintBackupDownloaded && backupPhraseOk(mintAckPhrase)) ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Confirm Saved
                    </button>
                  </div>

                  <div style={{ marginTop: '10px', fontSize: '12px', color: (mintBackupDownloaded && mintBackupAck) ? '#86efac' : '#fbbf24', fontWeight: 800 }}>
                    Backup status: {mintBackupDownloaded ? 'DOWNLOADED' : 'NOT DOWNLOADED'} • {mintBackupAck ? 'CONFIRMED' : 'NOT CONFIRMED'}
                  </div>

                  {!(mintBackupDownloaded && mintBackupAck) && (
                    <button
                      onClick={() => {
                        const discard = window.confirm('Discard this wallet? If it was funded, funds may be unrecoverable.');
                        if (!discard) return;
                        setMintedWallet(null);
                        setMintBackupDownloaded(false);
                        setMintBackupAck(false);
                        setMintAckPhrase('');
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '12px 14px',
                        fontSize: '12px',
                        fontWeight: 900,
                        background: 'rgba(239, 68, 68, 0.14)',
                        border: '1px solid rgba(239, 68, 68, 0.25)',
                        borderRadius: '10px',
                        color: '#fecaca',
                        cursor: 'pointer',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Discard Wallet
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch live XRP price from XRPL DEX (real market data)
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const client = new Client('wss://xrplcluster.com');
        await client.connect();
        
        // Get mid-market price from XRPL DEX orderbook (Bitstamp gateway)
        const bidBook = await client.request({
          command: 'book_offers',
          taker_gets: {
            currency: 'USD',
            issuer: 'rvYAfWj5gh67oV6fW32ZzP3Aw4Eubs59B' // Bitstamp USD
          },
          taker_pays: { currency: 'XRP' },
          limit: 1
        });
        
        const askBook = await client.request({
          command: 'book_offers',
          taker_gets: { currency: 'XRP' },
          taker_pays: {
            currency: 'USD',
            issuer: 'rvYAfWj5gh67oV6fW32ZzP3Aw4Eubs59B'
          },
          limit: 1
        });
        
        await client.disconnect();
        
        // Calculate mid-market price from best bid/ask
        let currentPrice = 2.35; // fallback
        
        if (bidBook.result.offers && bidBook.result.offers.length > 0) {
          const bidOffer = bidBook.result.offers[0] as unknown as BookOfferLike;
          const getsUsd = amountToNumber(bidOffer.TakerGets);
          const paysXrp = amountToNumber(bidOffer.TakerPays);
          const bidPrice = paysXrp > 0 ? (getsUsd / paysXrp) : 0;
          
          if (askBook.result.offers && askBook.result.offers.length > 0) {
            const askOffer = askBook.result.offers[0] as unknown as BookOfferLike;
            const getsXrp = amountToNumber(askOffer.TakerGets);
            const paysUsd = amountToNumber(askOffer.TakerPays);
            const askPrice = getsXrp > 0 ? (paysUsd / getsXrp) : 0;
            
            // Mid-market price
            currentPrice = (bidPrice + askPrice) / 2;
          }
        }
        
        setXrpPrice(currentPrice);
        
        // Update price history for chart
        setPriceHistory(prev => {
          const newHistory = [...prev, { 
            time: new Date().toLocaleTimeString(), 
            price: currentPrice
          }];
          // Keep only last 20 data points (10 minutes at 30s intervals)
          return newHistory.slice(-20);
        });
      } catch (error) {
        console.error('XRPL price fetch error:', error);
        // Keep previous price or use fallback
        setXrpPrice((prev) => (prev === 0 ? 2.35 : prev));
      }
    };
    
    fetchPrice();
    const interval = setInterval(fetchPrice, 30000); // Update every 30s
    return () => clearInterval(interval);
  }, []);

  // Fetch live XRPL order book
  useEffect(() => {
    const fetchOrderBook = async () => {
      if (page !== 'dex') return; // Only fetch when on DEX page
      
      setIsLoadingDex(true);
      try {
        const client = new Client('wss://xrplcluster.com');
        await client.connect();
        
        // Get XRP/USD order book
        const response = await client.request({
          command: 'book_offers',
          taker_gets: {
            currency: 'USD',
            issuer: 'rvYAfWj5gh67oV6fW32ZzP3Aw4Eubs59B' // Bitstamp USD issuer
          },
          taker_pays: {
            currency: 'XRP'
          },
          limit: 10
        });
        
        // Process bids
        // For this request: taker_gets = USD (IOU), taker_pays = XRP
        const bids = (response.result.offers || []).map((offerRaw) => {
          const offer = offerRaw as unknown as BookOfferLike;
          const getsUsd = amountToNumber(offer.TakerGets);
          const paysXrp = amountToNumber(offer.TakerPays);

          const priceUsdPerXrp = paysXrp > 0 ? (getsUsd / paysXrp) : 0;
          return {
            price: priceUsdPerXrp.toFixed(4),
            amount: paysXrp.toFixed(2)
          };
        }).slice(0, 5);
        
        // Get ask side
        const askResponse = await client.request({
          command: 'book_offers',
          taker_gets: {
            currency: 'XRP'
          },
          taker_pays: {
            currency: 'USD',
            issuer: 'rvYAfWj5gh67oV6fW32ZzP3Aw4Eubs59B'
          },
          limit: 10
        });
        
        // For this request: taker_gets = XRP, taker_pays = USD (IOU)
        const asks = (askResponse.result.offers || []).map((offerRaw) => {
          const offer = offerRaw as unknown as BookOfferLike;
          const getsXrp = amountToNumber(offer.TakerGets);
          const paysUsd = amountToNumber(offer.TakerPays);

          const priceUsdPerXrp = getsXrp > 0 ? (paysUsd / getsXrp) : 0;
          return {
            price: priceUsdPerXrp.toFixed(4),
            amount: getsXrp.toFixed(2)
          };
        }).slice(0, 5);
        
        setOrderBook({ bids, asks });
        
        // Fetch real recent trades from XRPL transaction history
        try {
          const tradesResponse = await client.request({
            command: 'account_tx',
            account: 'rvYAfWj5gh67oV6fW32ZzP3Aw4Eubs59B', // Bitstamp gateway (active trader)
            limit: 20,
            ledger_index_min: -1,
            ledger_index_max: -1
          });
          
          const recentXrpTrades: Array<{ time: string; price: string; amount: string; type: 'buy' | 'sell' }> = [];
          if (tradesResponse.result.transactions) {
            for (const txData of tradesResponse.result.transactions.slice(0, 5)) {
              const txUnknown: unknown = isRecord(txData) ? txData.tx : undefined;
              if (isPaymentTxLike(txUnknown)) {
                const amount = txUnknown.Amount;
                
                // Look for XRP/USD trades
                let xrpAmount = 0;
                let tradeType: 'buy' | 'sell' = 'buy';
                
                if (typeof amount === 'string') {
                  xrpAmount = Number(amount) / 1_000_000;
                  tradeType = 'buy';
                } else if (isIssuedAmountLike(amount)) {
                  const usdAmount = Number(amount.value);
                  xrpAmount = Math.round(usdAmount / xrpPrice);
                  tradeType = 'sell';
                }
                
                if (xrpAmount > 0) {
                  const closeTime = typeof txUnknown.date === 'number'
                    ? new Date((txUnknown.date + 946684800) * 1000)
                    : new Date();
                  recentXrpTrades.push({
                    time: closeTime.toLocaleTimeString(),
                    price: xrpPrice.toFixed(4),
                    amount: xrpAmount.toLocaleString(),
                    type: tradeType
                  });
                }
              }
            }
          }
          
          if (recentXrpTrades.length > 0) {
            setRecentTrades(recentXrpTrades);
          } else {
            // Fallback: show estimated recent trades based on current price
            setRecentTrades([
              { time: new Date(Date.now() - 240000).toLocaleTimeString(), price: xrpPrice.toFixed(4), amount: '1,250', type: 'buy' },
              { time: new Date(Date.now() - 180000).toLocaleTimeString(), price: (xrpPrice - 0.01).toFixed(4), amount: '850', type: 'sell' },
              { time: new Date(Date.now() - 120000).toLocaleTimeString(), price: (xrpPrice + 0.02).toFixed(4), amount: '2,100', type: 'buy' },
              { time: new Date(Date.now() - 60000).toLocaleTimeString(), price: xrpPrice.toFixed(4), amount: '950', type: 'sell' },
              { time: new Date().toLocaleTimeString(), price: xrpPrice.toFixed(4), amount: '1,800', type: 'buy' }
            ]);
          }
        } catch (tradeError) {
          console.error('Recent trades fetch error:', tradeError);
          // Use fallback data
          setRecentTrades([
            { time: new Date(Date.now() - 240000).toLocaleTimeString(), price: xrpPrice.toFixed(4), amount: '1,250', type: 'buy' },
            { time: new Date(Date.now() - 180000).toLocaleTimeString(), price: (xrpPrice - 0.01).toFixed(4), amount: '850', type: 'sell' },
            { time: new Date(Date.now() - 120000).toLocaleTimeString(), price: (xrpPrice + 0.02).toFixed(4), amount: '2,100', type: 'buy' },
            { time: new Date(Date.now() - 60000).toLocaleTimeString(), price: xrpPrice.toFixed(4), amount: '950', type: 'sell' },
            { time: new Date().toLocaleTimeString(), price: xrpPrice.toFixed(4), amount: '1,800', type: 'buy' }
          ]);
        }
        
        await client.disconnect();
      } catch (error) {
        console.error('Order book fetch error:', error);
        // Fallback to simulated data
        setOrderBook({
          bids: [
            { price: (xrpPrice - 0.01).toFixed(4), amount: '1250' },
            { price: (xrpPrice - 0.02).toFixed(4), amount: '2100' },
            { price: (xrpPrice - 0.03).toFixed(4), amount: '850' },
            { price: (xrpPrice - 0.04).toFixed(4), amount: '1450' },
            { price: (xrpPrice - 0.05).toFixed(4), amount: '3200' }
          ],
          asks: [
            { price: (xrpPrice + 0.01).toFixed(4), amount: '950' },
            { price: (xrpPrice + 0.02).toFixed(4), amount: '1800' },
            { price: (xrpPrice + 0.03).toFixed(4), amount: '1200' },
            { price: (xrpPrice + 0.04).toFixed(4), amount: '2500' },
            { price: (xrpPrice + 0.05).toFixed(4), amount: '900' }
          ]
        });
      } finally {
        setIsLoadingDex(false);
      }
    };
    
    fetchOrderBook();
    const interval = setInterval(fetchOrderBook, 15000); // Update every 15s
    return () => clearInterval(interval);
  }, [page, xrpPrice]);

  // Authentication Gate
  const AuthGate: React.FC<{children: React.ReactNode}> = ({ children }) => {
    const copy = async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        alert('Copied to clipboard.');
      } catch {
        alert('Copy failed. Select the text and copy manually.');
      }
    };

    // If logged in with a locally-generated wallet, enforce backup acknowledgement before allowing access.
    const needsBackup = !!userWallet?.seed && !(userWalletBackupDownloaded && userWalletBackupAck);

    if (!isLoggedIn || needsBackup) {
      const title = needsBackup ? 'Backup Required' : 'Authentication Required';
      const subtitle = needsBackup
        ? 'To prevent unrecoverable loss, you must export and confirm your wallet seed before proceeding.'
        : 'You must create a free account to use UNYKORN services.';

      return (
        <div style={{
          minHeight: '100vh',
          background: '#000000',
          color: '#e5e7eb',
          fontFamily: "'SF Pro Display', system-ui, sans-serif",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ maxWidth: '520px', padding: '40px', textAlign: 'center' }}>
            <div style={{
              fontSize: '48px',
              fontWeight: '300',
              marginBottom: '24px',
              background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {title}
            </div>
            <div style={{ fontSize: '16px', color: '#9ca3af', marginBottom: '24px', lineHeight: '1.6' }}>
              {subtitle}
            </div>

            {needsBackup && userWallet ? (
              <div style={{ textAlign: 'left' }}>
                <div style={{
                  padding: '18px',
                  background: 'rgba(251, 191, 36, 0.10)',
                  border: '1px solid rgba(251, 191, 36, 0.25)',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '12px', color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.10em', fontWeight: 900 }}>
                    Login Wallet Backup
                  </div>

                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>Address</div>
                  <div style={{ fontFamily: 'monospace', color: '#ffffff', wordBreak: 'break-all', marginTop: '6px' }}>
                    {userWallet.address}
                  </div>

                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>Seed</div>
                  <div style={{ fontFamily: 'monospace', color: '#fde68a', wordBreak: 'break-all', marginTop: '6px' }}>
                    {userWallet.seed}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '14px' }}>
                    <button
                      onClick={() => copy(userWallet.address)}
                      style={{
                        padding: '12px 14px',
                        background: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Copy Address
                    </button>
                    <button
                      onClick={() => copy(userWallet.seed)}
                      style={{
                        padding: '12px 14px',
                        background: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Copy Seed
                    </button>
                    <button
                      onClick={() => {
                        downloadWalletBackup('Login Wallet', userWallet);
                        setUserWalletBackupDownloaded(true);
                      }}
                      style={{
                        padding: '12px 14px',
                        background: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        color: '#000000',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 900,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Download Backup
                    </button>
                  </div>

                  <div style={{ marginTop: '14px', fontSize: '12px', color: '#9ca3af' }}>
                    Type <strong style={{ color: '#e5e7eb' }}>I SAVED IT</strong> to continue.
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginTop: '10px' }}>
                    <input
                      value={userWalletAckPhrase}
                      onChange={(e) => setUserWalletAckPhrase(e.target.value)}
                      placeholder="I SAVED IT"
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontFamily: 'monospace'
                      }}
                    />
                    <button
                      onClick={() => {
                        if (!backupPhraseOk(userWalletAckPhrase)) {
                          alert('Please type: I SAVED IT');
                          return;
                        }
                        if (!userWalletBackupDownloaded) {
                          alert('Download the backup first (or download again), then confirm.');
                          return;
                        }
                        setUserWalletBackupAck(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: (userWalletBackupDownloaded && backupPhraseOk(userWalletAckPhrase)) ? '#10b981' : 'rgba(16, 185, 129, 0.25)',
                        color: (userWalletBackupDownloaded && backupPhraseOk(userWalletAckPhrase)) ? '#000000' : '#9ca3af',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '800',
                        cursor: (userWalletBackupDownloaded && backupPhraseOk(userWalletAckPhrase)) ? 'pointer' : 'not-allowed',
                        letterSpacing: '0.10em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Confirm Saved
                    </button>
                  </div>

                  <div style={{ marginTop: '10px', fontSize: '12px', color: userWalletBackupDownloaded ? '#86efac' : '#fbbf24', fontWeight: 800 }}>
                    Backup: {userWalletBackupDownloaded ? 'DOWNLOADED' : 'NOT DOWNLOADED'} • Confirmation: {userWalletBackupAck ? 'OK' : 'PENDING'}
                  </div>
                </div>

                <button
                  onClick={() => {
                    const discard = window.confirm('Log out and discard this in-app wallet? If it was funded, funds may be unrecoverable.');
                    if (!discard) return;
                    setUserWallet(null);
                    setUserWalletBackupDownloaded(false);
                    setUserWalletBackupAck(false);
                    setUserWalletAckPhrase('');
                    setIsLoggedIn(false);
                    setLoginStep('login');
                  }}
                  style={{
                    marginTop: '16px',
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(239, 68, 68, 0.14)',
                    border: '1px solid rgba(239, 68, 68, 0.25)',
                    borderRadius: '10px',
                    color: '#fecaca',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 900,
                    letterSpacing: '0.10em',
                    textTransform: 'uppercase'
                  }}
                >
                  Logout / Discard
                </button>
              </div>
            ) : (
              <>
                {loginStep === 'login' ? (
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px', color: '#ffffff' }}>
                      Login with Existing Wallet
                    </div>
                    <input
                      placeholder="Enter your wallet address"
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontFamily: 'monospace',
                        marginBottom: '16px'
                      }}
                    />
                    <button
                      onClick={() => {
                        setIsLoggedIn(true);
                        setLoginStep('authenticated');
                      }}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: '#ffffff',
                        color: '#000000',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginBottom: '16px'
                      }}
                    >
                      LOGIN
                    </button>
                    <button
                      onClick={() => setLoginStep('register')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#60a5fa',
                        fontSize: '14px',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      Create Account
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px', color: '#ffffff' }}>
                      Create Account
                    </div>
                    <div style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '24px', lineHeight: '1.6' }}>
                      Generate a new XRPL wallet on mainnet. This becomes your secure login credential.
                      <div style={{ marginTop: '10px', color: '#fbbf24', fontSize: '13px', fontWeight: '600' }}>
                        ⚠️ Note: XRPL accounts require a 10 XRP reserve to activate (blockchain requirement, not a fee).
                      </div>
                      <div style={{ marginTop: '8px', color: '#fbbf24', fontSize: '13px' }}>
                        Guardrail: a backup JSON will be auto-downloaded and you must confirm it before proceeding.
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        const wallet = Wallet.generate();
                        const next = { address: wallet.address, seed: wallet.seed! };
                        setUserWallet(next);
                        setUserWalletBackupDownloaded(false);
                        setUserWalletBackupAck(false);
                        setUserWalletAckPhrase('');

                        downloadWalletBackup('Login Wallet', next);
                        setUserWalletBackupDownloaded(true);

                        setIsLoggedIn(true);
                        setLoginStep('authenticated');
                      }}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: '#ffffff',
                        color: '#000000',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginBottom: '16px'
                      }}
                    >
                      GENERATE WALLET (XRPL MAINNET)
                    </button>
                    <button
                      onClick={() => setLoginStep('login')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#60a5fa',
                        fontSize: '14px',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      Already have a wallet? Login
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    return <>{children}</>;
  };

  const Footer = () => (
    <footer style={{
      background: 'rgba(0, 0, 0, 0.95)',
      borderTop: '1px solid rgba(59, 130, 246, 0.1)',
      padding: isMobile ? '40px 20px' : '60px 48px',
      marginTop: 'auto'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '40px'
      }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#ffffff', marginBottom: '16px' }}>
            UNYKORN
          </div>
          <div style={{ fontSize: '14px', color: '#9ca3af', lineHeight: '1.6' }}>
            Precious metals dealer and XRPL settlement infrastructure. Weight-referenced assets only.
          </div>
        </div>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Legal
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button onClick={() => setPage('terms')} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '14px', textAlign: 'left', cursor: 'pointer', padding: 0 }}>Terms of Service</button>
            <button onClick={() => setPage('privacy')} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '14px', textAlign: 'left', cursor: 'pointer', padding: 0 }}>Privacy Policy</button>
            <button onClick={() => setPage('legal')} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '14px', textAlign: 'left', cursor: 'pointer', padding: 0 }}>Risk Disclosure</button>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#ffffff', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Support
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button onClick={() => setPage('faq')} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '14px', textAlign: 'left', cursor: 'pointer', padding: 0 }}>FAQ</button>
            <button onClick={() => setPage('contact')} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '14px', textAlign: 'left', cursor: 'pointer', padding: 0 }}>Contact</button>
            <XummLink href="https://livenet.xrpl.org/accounts/rpP12ND2K7ZRzXZBEUnQM2i18tMGytXnW1" style={{ color: '#9ca3af', fontSize: '14px', textDecoration: 'none' }}>Verify Our Wallet</XummLink>
          </div>
        </div>
      </div>
      <div style={{
        maxWidth: '1200px',
        margin: '40px auto 0',
        paddingTop: '24px',
        borderTop: '1px solid rgba(59, 130, 246, 0.1)',
        fontSize: '13px',
        color: '#6b7280',
        textAlign: 'center'
      }}>
        © 2026 UNYKORN. Not a registered investment advisor. Not FDIC insured.
      </div>
    </footer>
  );

  if (page === 'landing') {
    return <ProfessionalLanding onNavigate={(p) => setPage(p as PageKey)} />;
  }

  const NavBar = () => (
    <>
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(0, 0, 0, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
        padding: isMobile ? '16px 20px' : '20px 48px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 2000
      }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            cursor: 'pointer'
          }}
          onClick={() => setPage('landing')}
        >
          <div style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '700',
            background: 'linear-gradient(180deg, #e5e7eb 0%, #9ca3af 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.02em'
          }}>
            UNYKORN
          </div>
          <div style={{
            fontSize: '10px',
            color: '#6b7280',
            letterSpacing: '0.12em',
            textTransform: 'uppercase'
          }}>
            {buildStamp}
          </div>
        </div>
        
        {isMobile ? (
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            ☰
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '28px', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => setPage(item.key)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: page === item.key ? '#60a5fa' : '#9ca3af',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    transition: 'color 0.2s',
                    padding: '8px 0'
                  }}
                >
                  {item.labelUpper}
                </button>
              ))}
            </div>

            <button
              onClick={() => setMintWalletOpen(true)}
              style={{
                padding: '10px 14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                color: '#000000',
                fontSize: '12px',
                fontWeight: '800',
                cursor: 'pointer',
                letterSpacing: '0.10em',
                textTransform: 'uppercase'
              }}
              title="Mint Wallet"
            >
              Mint Wallet
            </button>

            {isAdmin ? (
              <button
                onClick={handleAdminLogout}
                style={{
                  padding: '10px 14px',
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: '800',
                  cursor: 'pointer',
                  letterSpacing: '0.10em',
                  textTransform: 'uppercase'
                }}
                title="Logout"
              >
                Logout
              </button>
            ) : (
              <button
                onClick={handleAdminLogin}
                style={{
                  padding: '10px 14px',
                  background: '#10b981',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: '800',
                  cursor: 'pointer',
                  letterSpacing: '0.10em',
                  textTransform: 'uppercase'
                }}
                title="Admin Login"
              >
                Admin
              </button>
            )}
          </div>
        )}
      </nav>

      <MintWalletModal />

      {isMobile && mobileMenuOpen && (
        <>
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1998
            }}
            onClick={() => setMobileMenuOpen(false)}
          />
          <div style={{
            position: 'fixed',
            top: '70px',
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.98)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(59, 130, 246, 0.2)',
            zIndex: 1999,
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            overflowY: 'auto'
          }}>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setMintWalletOpen(true);
              }}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'none',
                borderRadius: '12px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '700',
                textAlign: 'center',
                padding: '18px 20px',
                cursor: 'pointer',
                marginBottom: '24px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
            >
              💎 Mint Wallet
            </button>

            {isAdmin ? (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleAdminLogout();
                }}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  padding: '18px 20px',
                  cursor: 'pointer',
                  marginBottom: '24px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
              >
                🔓 Logout
              </button>
            ) : (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleAdminLogin();
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '700',
                  textAlign: 'center',
                  padding: '18px 20px',
                  cursor: 'pointer',
                  marginBottom: '24px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                🔐 Admin Login
              </button>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => {
                    setPage(item.key);
                    setMobileMenuOpen(false);
                  }}
                  style={{
                    background: page === item.key 
                      ? 'rgba(59, 130, 246, 0.15)' 
                      : 'rgba(255, 255, 255, 0.05)',
                    border: page === item.key 
                      ? '2px solid rgba(59, 130, 246, 0.5)' 
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    color: page === item.key ? '#60a5fa' : '#e5e7eb',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    padding: '16px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{item.icon}</span>
                  <span style={{ fontSize: '12px' }}>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );

  if (page === 'services') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />
        <FloatingHomeButton />

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px'
        }}>
          <div style={{
            fontSize: isMobile ? '36px' : '56px',
            fontWeight: '300',
            marginBottom: '24px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.02em'
          }}>
            XRPL System
          </div>

          <div style={{
            fontSize: isMobile ? '16px' : '20px',
            lineHeight: '1.8',
            color: '#d1d5db',
            fontWeight: '300',
            marginBottom: '64px',
            maxWidth: '900px'
          }}>
            Dealer attestation infrastructure for weight-referenced precious metals assets on XRPL.
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '24px',
            marginTop: '48px',
            marginBottom: '48px'
          }}>
            {serviceCards.map((item, i) => (
              <button
                key={i}
                onClick={() => setPage(item.page)}
                style={{
                  padding: isMobile ? '28px' : '36px',
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(20px)',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  fontSize: isMobile ? '20px' : '22px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  color: '#ffffff',
                  letterSpacing: '0.02em'
                }}>
                  {item.title}
                </div>
                <div style={{
                  fontSize: isMobile ? '14px' : '15px',
                  lineHeight: '1.7',
                  color: '#d1d5db',
                  fontWeight: '300',
                  marginBottom: '20px'
                }}>
                  {item.desc}
                </div>
                <div style={{
                  fontSize: isMobile ? '14px' : '15px',
                  fontWeight: '600',
                  color: '#60a5fa',
                  letterSpacing: '0.05em'
                }}>
                  {item.price}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (page === 'pof') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />
        <FloatingHomeButton />

        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px'
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '16px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Asset Verification
          </div>

          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '48px'
          }}>
            Step {pofStep} of 4: {
              pofStep === 1 ? 'Select Verification Type' :
              pofStep === 2 ? 'Network Selection' :
              pofStep === 3 ? 'Provide Details' :
              'Verification Report'
            }
          </div>

          {pofStep === 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px' }}>
              {[
                'Issued Asset Balance (IOU)',
                'Transaction History',
                'Proof-of-Funds Packet',
                'Legal / Audit Exhibit'
              ].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setVerificationType(type);
                    setPofStep(2);
                  }}
                  style={{
                    padding: '32px',
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)'}
                >
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                    {type}
                  </div>
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                    Generate verifiable documentation for {type.toLowerCase()}.
                  </div>
                </button>
              ))}
            </div>
          )}

          {pofStep === 2 && (
            <div style={{ maxWidth: '600px' }}>
              <div style={{
                padding: '32px',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                marginBottom: '32px'
              }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#10b981', marginBottom: '12px' }}>
                  XRP Ledger (Mainnet)
                </div>
                <div style={{ fontSize: '14px', color: '#d1d5db' }}>
                  Institutional settlement layer. Immutable ledger history.
                </div>
              </div>
              <button
                onClick={() => setPofStep(3)}
                style={{
                  padding: '16px 32px',
                  background: '#ffffff',
                  color: '#000000',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                CONFIRM NETWORK
              </button>
            </div>
          )}

          {pofStep === 3 && (
            <div style={{ maxWidth: '600px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '8px' }}>
                  Target Account / Transaction Hash
                </div>
                <input
                  value={verificationInput}
                  onChange={(e) => setVerificationInput(e.target.value)}
                  placeholder="r..."
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontFamily: 'monospace',
                    fontSize: '16px'
                  }}
                />
              </div>
              <button
                onClick={() => setPofStep(4)}
                style={{
                  padding: '16px 32px',
                  background: '#ffffff',
                  color: '#000000',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                GENERATE REPORT
              </button>
            </div>
          )}

          {pofStep === 4 && (
            <div style={{ maxWidth: '800px' }}>
              <div style={{
                padding: '40px',
                background: '#ffffff',
                color: '#000000',
                borderRadius: '4px',
                marginBottom: '32px'
              }}>
                <div style={{ 
                  borderBottom: '2px solid #000000', 
                  paddingBottom: '20px', 
                  marginBottom: '30px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '700' }}>VERIFICATION REPORT</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>{new Date().toLocaleDateString()}</div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '40px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', marginBottom: '4px' }}>TYPE</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>{verificationType}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', marginBottom: '4px' }}>NETWORK</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>XRP Ledger Mainnet</div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: '12px', color: '#666', textTransform: 'uppercase', marginBottom: '4px' }}>REFERENCE</div>
                    <div style={{ fontSize: '14px', fontFamily: 'monospace' }}>{verificationInput || 'N/A'}</div>
                  </div>
                </div>

                <div style={{ 
                  padding: '20px', 
                  background: '#f3f4f6', 
                  borderLeft: '4px solid #10b981',
                  marginBottom: '30px'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>STATUS: VERIFIED</div>
                  <div style={{ fontSize: '13px', color: '#374151' }}>
                    The requested asset data has been cryptographically verified against the XRP Ledger state.
                  </div>
                </div>

                <div style={{
                  padding: '24px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '12px',
                  marginBottom: '30px'
                }}>
                  <div style={{ 
                    fontSize: '13px', 
                    fontWeight: '600', 
                    color: '#10b981', 
                    textTransform: 'uppercase',
                    marginBottom: '12px' 
                  }}>
                    Dealer Attestation
                  </div>
                  <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.7' }}>
                    Unykorn acts as the originating metals dealer for all assets defined under this verification. 
                    Attestations assert physical facts only (metal, purity, weight, form, custody) at time of issuance. 
                    No price, yield, liquidity, or redemption value is implied.
                  </div>
                </div>

                <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: '1.5', textAlign: 'center' }}>
                  This document is generated by Unykorn dealer infrastructure. It represents cryptographic proof of physical weight at time of attestation. Not a price guarantee, investment guarantee, or redemption promise. Assets are weight-referenced only.
                </div>
              </div>

              <button
                onClick={() => {
                  setPofStep(1);
                  setVerificationInput('');
                  setVerificationType('');
                }}
                style={{
                  padding: '16px 32px',
                  background: 'transparent',
                  color: '#ffffff',
                  border: '1px solid #ffffff',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                START NEW VERIFICATION
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (page === 'pofaas') {
    return (
      <AuthGate>
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />

        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px'
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '16px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            POF-as-a-Service
          </div>

          <div style={{
            fontSize: isMobile ? '16px' : '18px',
            lineHeight: '1.8',
            color: '#d1d5db',
            marginBottom: '48px'
          }}>
            Mint your own wallet, establish trust lines, and generate on-ledger evidence for institutional proof-of-funds documentation (non-custodial).
          </div>

          <TxSafetyPanel />

          <div style={{
            padding: isMobile ? '24px' : '32px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981', marginBottom: '16px' }}>
              On-ledger Account Reference
            </div>
            <div style={{ fontSize: isMobile ? '36px' : '48px', fontWeight: '700', color: '#ffffff', marginBottom: '8px' }}>
              Balances vary
            </div>
            <div style={{ fontSize: '14px', color: '#9ca3af' }}>
              Account: <span style={{ fontFamily: 'monospace', color: '#e5e7eb' }}>rpP12ND2K7ZRzXZBEUnQM2i18tMGytXnW1</span>
            </div>
          </div>

          <div style={{
            padding: isMobile ? '28px' : '40px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#e5e7eb', marginBottom: '24px' }}>
              Step 1: Create Client Wallet
            </div>

            <div style={{
              padding: '20px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '14px', color: '#60a5fa', fontWeight: '600', marginBottom: '8px' }}>
                ℹ️ DUAL WALLET SYSTEM
              </div>
              <div style={{ fontSize: '14px', color: '#e5e7eb', lineHeight: '1.6' }}>
                • <strong>Your Login Wallet:</strong> {userWallet?.address || 'N/A'}<br/>
                • <strong>Client POF Wallet:</strong> Generated below for trust line issuance
              </div>
            </div>

            {!clientWallet ? (
              <>
                <div style={{ fontSize: '15px', color: '#9ca3af', lineHeight: '1.7', marginBottom: '24px' }}>
                  Generate a new XRPL wallet that will receive a <strong style={{ color: '#10b981' }}>trust line to the referenced issuer account</strong>.
                  This wallet is created on <strong style={{ color: '#60a5fa' }}>XRPL Mainnet</strong> and you control the private keys.
                </div>
                <button
                  onClick={async () => {
                    setGenerating(true);
                    try {
                      // Generate new wallet
                      const wallet = Wallet.generate();
                      const next = { address: wallet.address, seed: wallet.seed! };
                      setClientWallet(next);

                      // Backup guardrail (auto-download + require explicit acknowledgement)
                      setClientWalletBackupDownloaded(false);
                      setClientWalletBackupAck(false);
                      setClientWalletAckPhrase('');
                      downloadWalletBackup('Client POF Wallet', next);
                      setClientWalletBackupDownloaded(true);
                      
                      // Connect to XRPL and check if wallet exists
                      const client = new Client('wss://xrplcluster.com');
                      await client.connect();
                      
                      try {
                        await client.request({
                          command: 'account_info',
                          account: wallet.address,
                          ledger_index: 'validated'
                        });
                        console.log('Wallet exists on mainnet');
                      } catch (error: unknown) {
                        const maybe = error as { data?: { error?: string } };
                        if (maybe?.data?.error === 'actNotFound') {
                          console.log('New wallet created - needs activation with XRP');
                        }
                      }
                      
                      await client.disconnect();
                    } catch (error) {
                      console.error('Wallet creation error:', error);
                    } finally {
                      setGenerating(false);
                    }
                  }}
                  disabled={generating}
                  style={{
                    padding: '16px 48px',
                    fontSize: '15px',
                    fontWeight: '600',
                    background: generating ? 'rgba(59, 130, 246, 0.3)' : 'linear-gradient(135deg, rgba(37, 99, 235, 0.6) 0%, rgba(30, 64, 175, 0.5) 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '8px',
                    color: '#e5e7eb',
                    cursor: generating ? 'not-allowed' : 'pointer'
                  }}
                >
                  {generating ? 'GENERATING ON MAINNET...' : 'MINT CLIENT WALLET (XRPL MAINNET)'}
                </button>
              </>
            ) : (
              <div style={{
                padding: '24px',
                background: 'rgba(0, 0, 0, 0.6)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#10b981', marginBottom: '20px' }}>
                  ✓ CLIENT WALLET CREATED ON XRPL MAINNET
                </div>

                {!(clientWalletBackupDownloaded && clientWalletBackupAck) && (
                  <div style={{
                    marginBottom: '20px',
                    padding: '14px',
                    background: 'rgba(251, 191, 36, 0.10)',
                    border: '1px solid rgba(251, 191, 36, 0.25)',
                    borderRadius: '10px'
                  }}>
                    <div style={{ fontSize: '12px', color: '#fbbf24', fontWeight: 900, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                      Backup Required
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#e5e7eb', lineHeight: '1.5' }}>
                      A wallet backup JSON has been downloaded. You must confirm it before proceeding to Step 2.
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '12px' }}>
                      <button
                        onClick={() => {
                          downloadWalletBackup('Client POF Wallet', clientWallet);
                          setClientWalletBackupDownloaded(true);
                        }}
                        style={{
                          padding: '10px 12px',
                          background: '#ffffff',
                          border: 'none',
                          borderRadius: '10px',
                          color: '#000000',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 900,
                          letterSpacing: '0.10em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Download Backup
                      </button>
                    </div>
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af' }}>
                      Type <strong style={{ color: '#e5e7eb' }}>I SAVED IT</strong> and confirm.
                    </div>
                    <input
                      value={clientWalletAckPhrase}
                      onChange={(e) => setClientWalletAckPhrase(e.target.value)}
                      placeholder="I SAVED IT"
                      style={{
                        width: '100%',
                        padding: '14px',
                        marginTop: '10px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        fontSize: '14px',
                        fontFamily: 'monospace'
                      }}
                    />
                    <button
                      onClick={() => {
                        if (!backupPhraseOk(clientWalletAckPhrase)) {
                          alert('Please type: I SAVED IT');
                          return;
                        }
                        if (!clientWalletBackupDownloaded) {
                          alert('Download the backup first (or download again), then confirm.');
                          return;
                        }
                        setClientWalletBackupAck(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '14px',
                        marginTop: '10px',
                        background: (clientWalletBackupDownloaded && backupPhraseOk(clientWalletAckPhrase)) ? '#10b981' : 'rgba(16, 185, 129, 0.25)',
                        color: (clientWalletBackupDownloaded && backupPhraseOk(clientWalletAckPhrase)) ? '#000000' : '#9ca3af',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: 900,
                        cursor: (clientWalletBackupDownloaded && backupPhraseOk(clientWalletAckPhrase)) ? 'pointer' : 'not-allowed',
                        letterSpacing: '0.10em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Confirm Saved
                    </button>
                    <div style={{ marginTop: '10px', fontSize: '12px', color: clientWalletBackupDownloaded ? '#86efac' : '#fbbf24', fontWeight: 800 }}>
                      Backup: {clientWalletBackupDownloaded ? 'DOWNLOADED' : 'NOT DOWNLOADED'} • Confirmation: {clientWalletBackupAck ? 'OK' : 'PENDING'}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Client Wallet Address</div>
                  <div style={{
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    color: '#e5e7eb',
                    padding: '12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    borderRadius: '6px',
                    wordBreak: 'break-all'
                  }}>
                    {clientWallet.address}
                  </div>
                  <XummLink
                    href={`https://livenet.xrpl.org/accounts/${clientWallet.address}`}
                    style={{
                      fontSize: '12px',
                      color: '#60a5fa',
                      textDecoration: 'none',
                      marginTop: '8px',
                      display: 'inline-block'
                    }}
                  >
                    → View on XRPL Explorer
                  </XummLink>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>
                    Private Seed (STORE SECURELY)
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    color: '#fbbf24',
                    padding: '12px',
                    background: 'rgba(251, 191, 36, 0.1)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    borderRadius: '6px',
                    wordBreak: 'break-all'
                  }}>
                    {clientWallet.seed}
                  </div>
                </div>

                <div style={{
                  fontSize: '13px',
                  color: '#ef4444',
                  lineHeight: '1.6',
                  padding: '16px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px'
                }}>
                  CRITICAL: Save this seed immediately. Anyone with this seed controls the wallet.
                </div>

                <div style={{
                  marginTop: '14px',
                  fontSize: '12px',
                  color: (clientWalletBackupDownloaded && clientWalletBackupAck) ? '#86efac' : '#fbbf24',
                  fontWeight: 800
                }}>
                  Guardrail status: {(clientWalletBackupDownloaded && clientWalletBackupAck) ? 'CONFIRMED' : 'PENDING'}
                </div>
              </div>
            )}
          </div>

          {clientWallet && !(clientWalletBackupDownloaded && clientWalletBackupAck) && (
            <div style={{
              padding: isMobile ? '22px' : '28px',
              background: 'rgba(251, 191, 36, 0.08)',
              border: '1px solid rgba(251, 191, 36, 0.22)',
              borderRadius: '14px',
              marginBottom: '22px'
            }}>
              <div style={{ color: '#fbbf24', fontWeight: 900, letterSpacing: '0.10em', textTransform: 'uppercase', fontSize: '12px' }}>
                Step 2 Locked
              </div>
              <div style={{ marginTop: '8px', color: '#e5e7eb', fontSize: '14px', lineHeight: '1.6' }}>
                Confirm your client wallet backup above to continue.
              </div>
            </div>
          )}

          {clientWallet && (clientWalletBackupDownloaded && clientWalletBackupAck) && (
            <div style={{
              padding: isMobile ? '28px' : '40px',
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '600', color: '#e5e7eb', marginBottom: '24px' }}>
                Step 2: Request POF Amount
              </div>

              <div style={{ fontSize: '15px', color: '#9ca3af', lineHeight: '1.7', marginBottom: '24px' }}>
                Specify the USDT amount you need for proof of funds documentation. We will establish a trust line and issue visibility on your wallet.
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '8px' }}>
                  Requested Amount (USDT)
                </div>
                <input
                  type="number"
                  value={requestedAmount}
                  onChange={(e) => setRequestedAmount(e.target.value)}
                  placeholder="1000000"
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div style={{
                padding: '20px',
                background: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ fontSize: '14px', color: '#fbbf24', fontWeight: '600', marginBottom: '12px' }}>
                  PRICING
                </div>
                <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: '1.6' }}>
                  • Setup Fee: 500 XRP (one-time)
                  <br/>
                  • Service Fee: 2% of requested amount
                  <br/>
                  • Payment to: <span style={{ fontFamily: 'monospace', color: '#60a5fa' }}>rpP12ND2K7ZRzXZBEUnQM2i18tMGytXnW1</span>
                  <br/>
                  <br/>
                  Total: {requestedAmount ? `${(parseFloat(requestedAmount) * 0.02).toLocaleString()} USDT + 500 XRP` : 'Enter amount'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  disabled={!requestedAmount || parseFloat(requestedAmount) <= 0 || trustSetSubmitting}
                  onClick={() => {
                    const trustSetTx: TrustSet = {
                      TransactionType: 'TrustSet',
                      Account: clientWallet.address,
                      LimitAmount: {
                        currency: 'USDT',
                        issuer: 'rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm',
                        value: requestedAmount
                      },
                      Flags: 131072 // tfSetNoRipple
                    };

                    setTrustSetTxJson(trustSetTx as unknown as Record<string, unknown>);
                    setTrustSetReceipt(null);

                    console.log('=== XRPL TRUSTSET TRANSACTION (UNSIGNED) ===');
                    console.log('Network: Mainnet (wss://xrplcluster.com)');
                    console.log('Issuer: rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm');
                    console.log('Currency: USDT (issued IOU)');
                    console.log('Limit:', requestedAmount);
                    console.log('Account:', clientWallet.address);
                    console.log('Transaction JSON:', JSON.stringify(trustSetTx, null, 2));

                    alert(
                      `✓ TRUST LINE TRANSACTION PREPARED\n\nYou can now download/copy the JSON below, or sign & submit it to XRPL mainnet.\n\nClient Wallet: ${clientWallet.address}\nTrust Line Limit: ${requestedAmount} USDT\nIssuer: rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm`
                    );
                  }}
                  style={{
                    padding: '16px 28px',
                    fontSize: '14px',
                    fontWeight: '700',
                    background: (!requestedAmount || parseFloat(requestedAmount) <= 0 || trustSetSubmitting) ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    borderRadius: '8px',
                    color: (!requestedAmount || parseFloat(requestedAmount) <= 0 || trustSetSubmitting) ? '#9ca3af' : '#ffffff',
                    cursor: (!requestedAmount || parseFloat(requestedAmount) <= 0 || trustSetSubmitting) ? 'not-allowed' : 'pointer',
                    letterSpacing: '0.08em'
                  }}
                >
                  GENERATE TRUSTSET JSON
                </button>

                <button
                  disabled={!clientWallet || trustLineChecking}
                  onClick={async () => {
                    setTrustLineChecking(true);
                    setTrustLineSnapshot(null);

                    const client = new Client('wss://xrplcluster.com');
                    try {
                      await client.connect();

                      const respUnknown: unknown = await client.request({
                        command: 'account_lines',
                        account: clientWallet.address,
                        ledger_index: 'validated'
                      });

                      const respObj = (typeof respUnknown === 'object' && respUnknown !== null)
                        ? (respUnknown as Record<string, unknown>)
                        : null;
                      const resultObj = (respObj && typeof respObj.result === 'object' && respObj.result !== null)
                        ? (respObj.result as Record<string, unknown>)
                        : null;

                      setTrustLineSnapshot(resultObj);

                      // Attempt to detect the specific trust line
                      const lines = Array.isArray(resultObj?.lines) ? resultObj?.lines : [];
                      const found = (lines as Array<unknown>).some((lineUnknown) => {
                        if (typeof lineUnknown !== 'object' || lineUnknown === null) return false;
                        const line = lineUnknown as Record<string, unknown>;
                        return (
                          line.currency === 'USDT' &&
                          line.account === 'rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm'
                        );
                      });

                      alert(
                        found
                          ? '✓ Trust line detected on validated ledger.'
                          : 'Trust line not found yet on validated ledger. If you just submitted, wait a bit and try again.'
                      );
                    } catch (error) {
                      console.error('Trust line check error:', error);
                      alert(`Trust line check failed: ${formatErrorMessage(error)}`);
                    } finally {
                      try {
                        await client.disconnect();
                      } catch {
                        // ignore
                      }
                      setTrustLineChecking(false);
                    }
                  }}
                  style={{
                    padding: '16px 28px',
                    fontSize: '14px',
                    fontWeight: '700',
                    background: trustLineChecking ? 'rgba(59, 130, 246, 0.3)' : 'rgba(16, 185, 129, 0.12)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    borderRadius: '8px',
                    color: trustLineChecking ? '#9ca3af' : '#a7f3d0',
                    cursor: trustLineChecking ? 'not-allowed' : 'pointer',
                    letterSpacing: '0.08em'
                  }}
                >
                  {trustLineChecking ? 'CHECKING…' : 'VERIFY TRUST LINE'}
                </button>

                <button
                  disabled={!txArmed || !requestedAmount || parseFloat(requestedAmount) <= 0 || trustSetSubmitting}
                  onClick={async () => {
                    if (!ensureTxArmedOrAlert('TrustSet (create trust line)')) return;
                    if (!clientWallet.seed) {
                      alert('Missing wallet seed. Re-mint the client wallet to sign locally.');
                      return;
                    }

                    const ok = window.confirm(
                      'This will sign and submit a TrustSet transaction to XRPL MAINNET using the client wallet seed stored in your browser.\n\nThe wallet must be funded with XRP for reserve/fees. Continue?'
                    );
                    if (!ok) return;

                    setTrustSetSubmitting(true);
                    setTrustSetReceipt(null);

                    const client = new Client('wss://xrplcluster.com');
                    try {
                      await client.connect();

                      const trustSetTx: TrustSet = {
                        TransactionType: 'TrustSet',
                        Account: clientWallet.address,
                        LimitAmount: {
                          currency: 'USDT',
                          issuer: 'rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm',
                          value: requestedAmount
                        },
                        Flags: 131072 // tfSetNoRipple
                      };

                      const wallet = Wallet.fromSeed(clientWallet.seed);
                      const prepared = await client.autofill(trustSetTx);
                      const signed = wallet.sign(prepared);

                      console.log('=== XRPL TRUSTSET SUBMIT (SIGNED) ===');
                      console.log('hash:', signed.hash);
                      console.log('prepared:', prepared);

                      const submitResp = await client.submitAndWait(signed.tx_blob);
                      const submitRespUnknown: unknown = submitResp;
                      const submitObj = (typeof submitRespUnknown === 'object' && submitRespUnknown !== null)
                        ? (submitRespUnknown as Record<string, unknown>)
                        : {};
                      const resultObj = (typeof submitObj.result === 'object' && submitObj.result !== null)
                        ? (submitObj.result as Record<string, unknown>)
                        : null;

                      const metaObj = (resultObj && typeof resultObj.meta === 'object' && resultObj.meta !== null)
                        ? (resultObj.meta as Record<string, unknown>)
                        : null;

                      const txResultFromMeta = metaObj && typeof metaObj.TransactionResult === 'string'
                        ? metaObj.TransactionResult
                        : undefined;
                      const txResultFromEngine = resultObj && typeof resultObj.engine_result === 'string'
                        ? resultObj.engine_result
                        : undefined;

                      const txResult = txResultFromMeta || txResultFromEngine;

                      const ledgerIndexRaw = resultObj?.ledger_index;
                      const ledgerIndex = typeof ledgerIndexRaw === 'number' ? ledgerIndexRaw : undefined;
                      const validated = resultObj?.validated === true;

                      setTrustSetTxJson(prepared as unknown as Record<string, unknown>);
                      setTrustSetReceipt({
                        hash: signed.hash,
                        validated,
                        ledgerIndex,
                        transactionResult: txResult
                      });

                      alert(
                        `✓ TRUST LINE SUBMITTED\n\nResult: ${txResult || 'submitted'}\nValidated: ${validated ? 'YES' : 'NO/UNKNOWN'}\n\nTransaction: ${signed.hash}\nExplorer:\nhttps://livenet.xrpl.org/transactions/${signed.hash}`
                      );
                    } catch (error) {
                      console.error('TrustSet submit error:', error);
                      alert(`TrustSet submit failed: ${formatErrorMessage(error)}`);
                    } finally {
                      try {
                        await client.disconnect();
                      } catch {
                        // ignore
                      }
                      setTrustSetSubmitting(false);
                    }
                  }}
                  style={{
                    padding: '16px 28px',
                    fontSize: '14px',
                    fontWeight: '800',
                    background: (!txArmed || !requestedAmount || parseFloat(requestedAmount) <= 0 || trustSetSubmitting) ? 'rgba(59, 130, 246, 0.3)' : '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    color: (!txArmed || !requestedAmount || parseFloat(requestedAmount) <= 0 || trustSetSubmitting) ? '#9ca3af' : '#000000',
                    cursor: (!txArmed || !requestedAmount || parseFloat(requestedAmount) <= 0 || trustSetSubmitting) ? 'not-allowed' : 'pointer',
                    letterSpacing: '0.08em'
                  }}
                >
                  {trustSetSubmitting ? 'SUBMITTING…' : 'SIGN & SUBMIT (MAINNET)'}
                </button>
              </div>

              {(trustSetTxJson || trustSetReceipt) && (
                <div style={{
                  marginTop: '20px',
                  padding: '18px',
                  background: 'rgba(0, 0, 0, 0.45)',
                  border: '1px solid rgba(59, 130, 246, 0.18)',
                  borderRadius: '10px'
                }}>
                  <div style={{ fontSize: '13px', color: '#93c5fd', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '10px' }}>
                    Transaction Output
                  </div>

                  {trustSetReceipt && (
                    <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: '1.7', marginBottom: '14px' }}>
                      <div><strong style={{ color: '#10b981' }}>Hash:</strong> <span style={{ fontFamily: 'monospace' }}>{trustSetReceipt.hash}</span></div>
                      <div><strong style={{ color: '#60a5fa' }}>Result:</strong> {trustSetReceipt.transactionResult || 'submitted'}</div>
                      <div><strong style={{ color: '#60a5fa' }}>Validated:</strong> {trustSetReceipt.validated ? 'YES' : 'NO/UNKNOWN'}</div>
                      {typeof trustSetReceipt.ledgerIndex === 'number' && (
                        <div><strong style={{ color: '#60a5fa' }}>Ledger:</strong> {trustSetReceipt.ledgerIndex}</div>
                      )}
                      <div style={{ marginTop: '8px' }}>
                        <XummLink
                          href={`https://livenet.xrpl.org/transactions/${trustSetReceipt.hash}`}
                          style={{ color: '#93c5fd', textDecoration: 'none', fontSize: '13px' }}
                        >
                          → View transaction on XRPL Explorer
                        </XummLink>
                      </div>
                    </div>
                  )}

                  {trustSetTxJson && (
                    <div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                        Prepared transaction JSON (never includes your seed)
                      </div>
                      <pre style={{
                        margin: 0,
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.45)',
                        border: '1px solid rgba(255, 255, 255, 0.10)',
                        borderRadius: '8px',
                        color: '#e5e7eb',
                        fontSize: '12px',
                        overflowX: 'auto',
                        maxHeight: '260px'
                      }}>
                        {JSON.stringify(trustSetTxJson, null, 2)}
                      </pre>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '12px' }}>
                    {trustSetTxJson && (
                      <button
                        onClick={() => downloadJson(`uny-trustset-${clientWallet.address}.json`, trustSetTxJson)}
                        style={{
                          padding: '10px 12px',
                          fontSize: '12px',
                          fontWeight: '700',
                          background: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.15)',
                          borderRadius: '10px',
                          color: '#ffffff',
                          cursor: 'pointer',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Download JSON
                      </button>
                    )}

                    {trustSetReceipt && (
                      <button
                        onClick={() => {
                          const proof = {
                            type: 'XRPL_TRUSTSET_RECEIPT',
                            network: 'XRPL Mainnet',
                            websocket: 'wss://xrplcluster.com',
                            createdAt: new Date().toISOString(),
                            account: clientWallet.address,
                            issuer: 'rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm',
                            currency: 'USDT',
                            limit: requestedAmount,
                            txHash: trustSetReceipt.hash,
                            validated: trustSetReceipt.validated,
                            ledgerIndex: trustSetReceipt.ledgerIndex,
                            transactionResult: trustSetReceipt.transactionResult,
                            postConditions: {
                              note: 'Optional snapshot from account_lines on validated ledger. Use VERIFY TRUST LINE to refresh.',
                              accountLinesResult: trustLineSnapshot
                            }
                          };
                          downloadJson(`uny-proof-${trustSetReceipt.hash}.json`, proof);
                        }}
                        style={{
                          padding: '10px 12px',
                          fontSize: '12px',
                          fontWeight: '800',
                          background: '#10b981',
                          border: 'none',
                          borderRadius: '10px',
                          color: '#000000',
                          cursor: 'pointer',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Download Proof
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div style={{
                fontSize: '13px',
                color: '#9ca3af',
                marginTop: '24px',
                lineHeight: '1.6'
              }}>
                <strong style={{ color: '#60a5fa' }}>Direct XRPL Mainnet Connection:</strong> Generate the TrustSet JSON, or sign & submit it from your browser. Your seed is never added to the JSON output.
              </div>
            </div>
          )}
        </div>
      </div>
      </AuthGate>
    );
  }

  if (page === 'dex') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />
        <XummModal />

        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px'
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '16px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Settlement (DEX)
          </div>

          <div style={{
            fontSize: '16px',
            color: '#9ca3af',
            marginBottom: '32px'
          }}>
            Cryptographic settlement via XRPL decentralized exchange. 100% accurate live market data.
          </div>

          <TxSafetyPanel />

          <div style={{
            padding: isMobile ? '18px' : '20px',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(15, 23, 42, 0.25) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.20)',
            borderRadius: '16px',
            marginBottom: '22px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 900 }}>
                  Signing Mode
                </div>
                <div style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#ffffff', marginTop: '8px' }}>
                  {useXumm ? 'Xaman (XUMM) — no seeds in browser' : 'Local seed — in-browser signing'}
                </div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px', lineHeight: 1.6, maxWidth: '980px' }}>
                  Recommended: use Xaman for public demos and real funds. Local seed mode is for dev/testing only.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={() => persistUseXumm(!useXumm)}
                  style={{
                    padding: '12px 14px',
                    fontSize: '12px',
                    fontWeight: 900,
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.14)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  {useXumm ? 'Use Local Seed' : 'Use Xaman'}
                </button>

                {useXumm && (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          const status = await runXummFlow({ TransactionType: 'SignIn' }, 'Connect Xaman Wallet');
                          const meta = status.meta || {};
                          if (meta.cancelled) {
                            alert('Xaman connect cancelled.');
                            return;
                          }
                          if (meta.expired) {
                            alert('Xaman connect expired.');
                            return;
                          }
                          if (!meta.signed) {
                            alert('Xaman connect not signed.');
                            return;
                          }

                          const acct = typeof status.response?.account === 'string' ? status.response?.account : '';
                          if (!acct) {
                            alert('No XRPL account returned from Xaman SignIn.');
                            return;
                          }

                          persistXummAccount(acct);
                          alert(`✓ Connected via Xaman\n\nAccount: ${acct}`);
                        } catch (error) {
                          alert(`Xaman connect failed: ${formatErrorMessage(error)}`);
                        }
                      }}
                      style={{
                        padding: '12px 14px',
                        fontSize: '12px',
                        fontWeight: 900,
                        background: '#ffffff',
                        border: 'none',
                        borderRadius: '10px',
                        color: '#000000',
                        cursor: 'pointer',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase'
                      }}
                    >
                      {xummAccount ? 'Reconnect Xaman' : 'Connect Xaman'}
                    </button>

                    {xummAccount && (
                      <button
                        onClick={() => {
                          persistXummAccount('');
                          alert('Xaman disconnected (local session cleared).');
                        }}
                        style={{
                          padding: '12px 14px',
                          fontSize: '12px',
                          fontWeight: 900,
                          background: 'rgba(239, 68, 68, 0.14)',
                          border: '1px solid rgba(239, 68, 68, 0.22)',
                          borderRadius: '10px',
                          color: '#fecaca',
                          cursor: 'pointer',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase'
                        }}
                      >
                        Disconnect
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {useXumm && (
              <div style={{
                marginTop: '14px',
                padding: '12px 14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>Connected account</div>
                <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#e5e7eb', wordBreak: 'break-all' }}>
                  {xummAccount || 'Not connected (click Connect Xaman)'}
                </div>

                <div style={{ height: '12px' }} />

                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Payload / UUID lookup (Xaman dashboard)
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    value={payloadLookupUuid}
                    onChange={(e) => setPayloadLookupUuid(e.target.value)}
                    placeholder="479c3e92-b100-47eb-8c39-12ccf645d5dd"
                    style={{
                      flex: '1 1 320px',
                      padding: '12px',
                      background: 'rgba(0, 0, 0, 0.45)',
                      border: '1px solid rgba(255, 255, 255, 0.14)',
                      borderRadius: '10px',
                      color: '#ffffff',
                      fontSize: '13px'
                    }}
                  />
                  <button
                    onClick={() => void lookupXummPayload(payloadLookupUuid)}
                    disabled={payloadLookupBusy}
                    style={{
                      padding: '12px 14px',
                      fontSize: '12px',
                      fontWeight: 900,
                      background: payloadLookupBusy ? 'rgba(255, 255, 255, 0.10)' : 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.14)',
                      borderRadius: '10px',
                      color: '#ffffff',
                      cursor: payloadLookupBusy ? 'not-allowed' : 'pointer',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase'
                    }}
                  >
                    {payloadLookupBusy ? 'Checking…' : 'Check UUID'}
                  </button>
                </div>

                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', lineHeight: 1.5 }}>
                  Note: A payload UUID is <span style={{ color: '#e5e7eb' }}>not</span> an XRPL transaction hash. You only get an XRPL <code style={{ color: '#e5e7eb' }}>txid</code> after the payload is opened and signed.
                </div>

                {payloadLookupError && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#fecaca' }}>
                    {payloadLookupError}
                  </div>
                )}

                {payloadLookupStatus && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                      <div style={{ padding: '10px 12px', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '10px' }}>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>Status</div>
                        <div style={{ fontSize: '12px', color: '#e5e7eb', marginTop: '6px', lineHeight: 1.55 }}>
                          <div>exists: <b>{String(payloadLookupStatus.meta?.exists ?? '')}</b></div>
                          <div>opened: <b>{String(payloadLookupStatus.meta?.app_opened ?? '')}</b></div>
                          <div>opened_by_deeplink: <b>{String(payloadLookupStatus.meta?.opened_by_deeplink ?? '')}</b></div>
                          <div>signed: <b>{String(payloadLookupStatus.meta?.signed ?? '')}</b></div>
                          <div>resolved: <b>{String(payloadLookupStatus.meta?.resolved ?? '')}</b></div>
                          <div>cancelled: <b>{String(payloadLookupStatus.meta?.cancelled ?? '')}</b></div>
                          <div>expired: <b>{String(payloadLookupStatus.meta?.expired ?? '')}</b></div>
                        </div>
                      </div>

                      <div style={{ padding: '10px 12px', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: '10px' }}>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>Result</div>
                        <div style={{ fontSize: '12px', color: '#e5e7eb', marginTop: '6px', lineHeight: 1.55, wordBreak: 'break-all' }}>
                          <div>account: <b>{String(payloadLookupStatus.response?.account ?? '')}</b></div>
                          <div>txid: <b>{String(payloadLookupStatus.response?.txid ?? '')}</b></div>
                          {typeof payloadLookupStatus.response?.txid === 'string' && payloadLookupStatus.response?.txid && (
                            <div style={{ marginTop: '6px' }}>
                              <XummLink
                                href={`https://livenet.xrpl.org/transactions/${encodeURIComponent(payloadLookupStatus.response?.txid)}`}
                                style={{ color: '#93c5fd' }}
                              >
                                View on XRPL Explorer
                              </XummLink>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Instant Settlement (Real XRPL Payment) */}
          <div style={{
            padding: isMobile ? '22px' : '28px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(15, 23, 42, 0.25) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.25)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6ee7b7', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 800 }}>
                  Instant Settlement
                </div>
                <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 600, color: '#ffffff', marginTop: '8px' }}>
                  Send XRP on XRPL Mainnet
                </div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.6, maxWidth: '900px' }}>
                  This signs and submits a real <span style={{ color: '#a7f3d0', fontWeight: 700 }}>Payment</span> transaction to XRPL mainnet using your in-app wallet seed.
                  The sending wallet must be funded for fees/reserve.
                </div>
              </div>

              <div style={{
                padding: '12px 14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>
                  Sender (current session)
                </div>
                <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#e5e7eb', wordBreak: 'break-all' }}>
                  {useXumm
                    ? (xummAccount || 'Not connected (Connect Xaman above)')
                    : (userWallet?.address || 'Not set (create/free account)')}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 0.6fr', gap: '16px', marginTop: '18px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Destination Address
                </div>
                <input
                  value={settlementDestination}
                  onChange={(e) => setSettlementDestination(e.target.value.trim())}
                  placeholder="r..."
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Amount (XRP)
                </div>
                <input
                  value={settlementAmountXrp}
                  onChange={(e) => setSettlementAmountXrp(e.target.value)}
                  placeholder="10"
                  inputMode="decimal"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '0.6fr 1.4fr', gap: '16px', marginTop: '14px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Destination Tag (optional)
                </div>
                <input
                  value={settlementDestinationTag}
                  onChange={(e) => setSettlementDestinationTag(e.target.value)}
                  placeholder="12345"
                  inputMode="numeric"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Memo (optional)
                </div>
                <input
                  value={settlementMemo}
                  onChange={(e) => setSettlementMemo(e.target.value)}
                  placeholder="Invoice #123 / Reference"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
              <button
                disabled={!txArmed || settlementSubmitting || !settlementDestination || !settlementAmountXrp || (useXumm ? !xummAccount : !userWallet?.seed)}
                onClick={async () => {
                  if (!ensureTxArmedOrAlert('XRP Payment')) return;
                  const senderAddress = useXumm ? xummAccount : userWallet?.address;
                  if (!senderAddress) {
                    alert(useXumm ? 'Connect Xaman first so we have a sender address.' : 'Create a free account first so we have a sender address.');
                    return;
                  }
                  if (!useXumm && !userWallet?.seed) {
                    alert('No sender seed available. Create a free account (wallet) first so the app can sign transactions.');
                    return;
                  }

                  const amountNum = Number(settlementAmountXrp);
                  if (!Number.isFinite(amountNum) || amountNum <= 0) {
                    alert('Enter a valid XRP amount.');
                    return;
                  }

                  const tagNum = settlementDestinationTag ? Number(settlementDestinationTag) : undefined;
                  if (settlementDestinationTag && (!Number.isInteger(tagNum) || (tagNum as number) < 0)) {
                    alert('Destination tag must be a non-negative integer.');
                    return;
                  }

                  const ok = window.confirm(
                    `Submit an XRPL MAINNET payment?\n\nFrom: ${senderAddress}\nTo: ${settlementDestination}\nAmount: ${settlementAmountXrp} XRP${tagNum !== undefined ? `\nTag: ${tagNum}` : ''}`
                  );
                  if (!ok) return;

                  setSettlementSubmitting(true);
                  setSettlementReceipt(null);

                  const client = new Client('wss://xrplcluster.com');
                  try {
                    await client.connect();

                    const baseTx: Payment = {
                      TransactionType: 'Payment',
                      Account: senderAddress,
                      Destination: settlementDestination,
                      Amount: xrpToDrops(amountNum)
                    };

                    const tx: Payment = {
                      ...baseTx,
                      ...(tagNum !== undefined ? { DestinationTag: tagNum as number } : {})
                    };

                    if (settlementMemo.trim()) {
                      (tx as unknown as Record<string, unknown>).Memos = [
                        { Memo: { MemoData: utf8ToHex(settlementMemo.trim()) } }
                      ];
                    }

                    const prepared = await client.autofill(tx);
                    setSettlementPreparedTx(prepared as unknown as Record<string, unknown>);

                    if (useXumm) {
                      const { txid } = await xummSubmit(prepared as unknown as Record<string, unknown>, 'XRP Payment');
                      if (!txid) throw new Error('No txid returned from Xaman.');

                      const validatedResult = await waitForValidatedTx(client, txid);
                      const metaObj = (validatedResult && typeof validatedResult.meta === 'object' && validatedResult.meta !== null)
                        ? (validatedResult.meta as Record<string, unknown>)
                        : null;
                      const txResultFromMeta = metaObj && typeof metaObj.TransactionResult === 'string'
                        ? metaObj.TransactionResult
                        : undefined;
                      const txResultFromEngine = validatedResult && typeof validatedResult.engine_result === 'string'
                        ? validatedResult.engine_result
                        : undefined;
                      const txResult = txResultFromMeta || txResultFromEngine;

                      const ledgerIndexRaw = validatedResult?.ledger_index;
                      const ledgerIndex = typeof ledgerIndexRaw === 'number' ? ledgerIndexRaw : undefined;
                      const validated = validatedResult?.validated === true;

                      setSettlementReceipt({ hash: txid, validated, ledgerIndex, transactionResult: txResult });

                      alert(
                        `✓ PAYMENT SUBMITTED (Xaman)\n\nResult: ${txResult || 'submitted'}\nValidated: ${validated ? 'YES' : 'NO/UNKNOWN'}\n\nTransaction: ${txid}\nExplorer:\nhttps://livenet.xrpl.org/transactions/${txid}`
                      );
                    } else {
                      const wallet = Wallet.fromSeed(userWallet!.seed);
                      const signed = wallet.sign(prepared);

                      const submitResp = await client.submitAndWait(signed.tx_blob);
                      const submitRespUnknown: unknown = submitResp;
                      const submitObj = (typeof submitRespUnknown === 'object' && submitRespUnknown !== null)
                        ? (submitRespUnknown as Record<string, unknown>)
                        : {};
                      const resultObj = (typeof submitObj.result === 'object' && submitObj.result !== null)
                        ? (submitObj.result as Record<string, unknown>)
                        : null;

                      const metaObj = (resultObj && typeof resultObj.meta === 'object' && resultObj.meta !== null)
                        ? (resultObj.meta as Record<string, unknown>)
                        : null;
                      const txResultFromMeta = metaObj && typeof metaObj.TransactionResult === 'string'
                        ? metaObj.TransactionResult
                        : undefined;
                      const txResultFromEngine = resultObj && typeof resultObj.engine_result === 'string'
                        ? resultObj.engine_result
                        : undefined;
                      const txResult = txResultFromMeta || txResultFromEngine;

                      const ledgerIndexRaw = resultObj?.ledger_index;
                      const ledgerIndex = typeof ledgerIndexRaw === 'number' ? ledgerIndexRaw : undefined;
                      const validated = resultObj?.validated === true;

                      setSettlementReceipt({
                        hash: signed.hash,
                        validated,
                        ledgerIndex,
                        transactionResult: txResult
                      });

                      alert(
                        `✓ PAYMENT SUBMITTED\n\nResult: ${txResult || 'submitted'}\nValidated: ${validated ? 'YES' : 'NO/UNKNOWN'}\n\nTransaction: ${signed.hash}\nExplorer:\nhttps://livenet.xrpl.org/transactions/${signed.hash}`
                      );
                    }
                  } catch (error) {
                    console.error('Payment submit error:', error);
                    alert(`Payment submit failed: ${formatErrorMessage(error)}`);
                  } finally {
                    try {
                      await client.disconnect();
                    } catch {
                      // ignore
                    }
                    setSettlementSubmitting(false);
                  }
                }}
                style={{
                  padding: '14px 18px',
                  fontSize: '13px',
                  fontWeight: 800,
                  background: (!txArmed || settlementSubmitting || !settlementDestination || !settlementAmountXrp) ? 'rgba(16, 185, 129, 0.25)' : '#10b981',
                  border: 'none',
                  borderRadius: '10px',
                  color: (!txArmed || settlementSubmitting || !settlementDestination || !settlementAmountXrp) ? '#9ca3af' : '#000000',
                  cursor: (!txArmed || settlementSubmitting || !settlementDestination || !settlementAmountXrp) ? 'not-allowed' : 'pointer',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                {settlementSubmitting ? 'Submitting…' : 'Sign & Submit Payment'}
              </button>

              {settlementPreparedTx && (
                <button
                  onClick={() => {
                    const fromAddress = useXumm ? xummAccount : userWallet?.address;
                    downloadJson(`uny-payment-${fromAddress || 'sender'}.json`, settlementPreparedTx);
                  }}
                  style={{
                    padding: '14px 18px',
                    fontSize: '13px',
                    fontWeight: 800,
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  Download TX JSON
                </button>
              )}

              {settlementReceipt && (
                <button
                  onClick={() => {
                    const fromAddress = useXumm ? xummAccount : userWallet?.address;
                    const proof = {
                      type: 'XRPL_PAYMENT_RECEIPT',
                      network: 'XRPL Mainnet',
                      websocket: 'wss://xrplcluster.com',
                      createdAt: new Date().toISOString(),
                      from: fromAddress,
                      to: settlementDestination,
                      amountXrp: settlementAmountXrp,
                      destinationTag: settlementDestinationTag ? Number(settlementDestinationTag) : undefined,
                      memo: settlementMemo.trim() ? settlementMemo.trim() : undefined,
                      txHash: settlementReceipt.hash,
                      validated: settlementReceipt.validated,
                      ledgerIndex: settlementReceipt.ledgerIndex,
                      transactionResult: settlementReceipt.transactionResult,
                      preparedTx: settlementPreparedTx
                    };
                    downloadJson(`uny-settlement-proof-${settlementReceipt.hash}.json`, proof);
                  }}
                  style={{
                    padding: '14px 18px',
                    fontSize: '13px',
                    fontWeight: 900,
                    background: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#000000',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  Download Proof
                </button>
              )}
            </div>

            {settlementReceipt && (
              <div style={{
                marginTop: '14px',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(16, 185, 129, 0.18)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Latest Payment Receipt
                </div>
                <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: 1.7 }}>
                  <div><strong style={{ color: '#10b981' }}>Hash:</strong> <span style={{ fontFamily: 'monospace' }}>{settlementReceipt.hash}</span></div>
                  <div><strong style={{ color: '#60a5fa' }}>Result:</strong> {settlementReceipt.transactionResult || 'submitted'}</div>
                  <div><strong style={{ color: '#60a5fa' }}>Validated:</strong> {settlementReceipt.validated ? 'YES' : 'NO/UNKNOWN'}</div>
                  <XummLink
                    href={`https://livenet.xrpl.org/transactions/${settlementReceipt.hash}`}
                    style={{ color: '#93c5fd', textDecoration: 'none', fontSize: '13px', display: 'inline-block', marginTop: '6px' }}
                  >
                    → View on XRPL Explorer
                  </XummLink>
                </div>
              </div>
            )}
          </div>

          {/* Issued Asset Settlement (IOU Payment) */}
          <div style={{
            padding: isMobile ? '22px' : '28px',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.14) 0%, rgba(15, 23, 42, 0.25) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.22)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 800 }}>
                  Issued Asset Settlement
                </div>
                <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 600, color: '#ffffff', marginTop: '8px' }}>
                  Send an IOU (USDT / USD)
                </div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.6, maxWidth: '980px' }}>
                  This submits a real XRPL <span style={{ color: '#93c5fd', fontWeight: 700 }}>Payment</span> where <span style={{ color: '#bfdbfe', fontWeight: 700 }}>Amount</span> is an issued currency.
                  Both sender and recipient generally need trust lines to the issuer.
                </div>
              </div>

              <div style={{
                padding: '12px 14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>
                  Sender (current session)
                </div>
                <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#e5e7eb', wordBreak: 'break-all' }}>
                  {useXumm
                    ? (xummAccount || 'Not connected (Connect Xaman above)')
                    : (userWallet?.address || 'Not set (create/free account)')}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginTop: '18px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Destination Address
                </div>
                <input
                  value={iouDestination}
                  onChange={(e) => setIouDestination(e.target.value.trim())}
                  placeholder="r..."
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(59, 130, 246, 0.22)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 1.3fr', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                    Currency
                  </div>
                  <select
                    value={iouCurrency}
                    onChange={(e) => setIouCurrency((e.target.value === 'USD' ? 'USD' : 'USDT'))}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: '10px',
                      color: '#e5e7eb',
                      fontSize: '14px'
                    }}
                  >
                    <option value="USDT">USDT</option>
                    <option value="USD">USD</option>
                  </select>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                    Issuer
                  </div>
                  <input
                    value={iouIssuer}
                    onChange={(e) => setIouIssuer(e.target.value.trim())}
                    placeholder="rIssuer..."
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: '10px',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '0.7fr 0.7fr 1.6fr', gap: '16px', marginTop: '14px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Amount ({iouCurrency})
                </div>
                <input
                  value={iouAmount}
                  onChange={(e) => setIouAmount(e.target.value)}
                  placeholder="1000"
                  inputMode="decimal"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(59, 130, 246, 0.22)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Destination Tag (optional)
                </div>
                <input
                  value={iouDestinationTag}
                  onChange={(e) => setIouDestinationTag(e.target.value)}
                  placeholder="12345"
                  inputMode="numeric"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                  Memo (optional)
                </div>
                <input
                  value={iouMemo}
                  onChange={(e) => setIouMemo(e.target.value)}
                  placeholder="Invoice #123 / Reference"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
              <button
                disabled={iouChecking || !iouDestination || !iouIssuer || (useXumm ? !xummAccount : !userWallet?.address)}
                onClick={async () => {
                  const senderAddress = useXumm ? xummAccount : userWallet?.address;
                  if (!senderAddress) {
                    alert(useXumm ? 'Connect Xaman first so we have a sender address.' : 'Create a free account first so we have a sender address.');
                    return;
                  }
                  if (!iouDestination || !iouIssuer) {
                    alert('Destination and issuer are required.');
                    return;
                  }

                  setIouChecking(true);
                  setIouSenderLines(null);
                  setIouDestLines(null);
                  setIouPreflight(null);

                  const client = new Client('wss://xrplcluster.com');
                  try {
                    await client.connect();

                    // Destination existence check
                    let destExists = true;
                    try {
                      await client.request({
                        command: 'account_info',
                        account: iouDestination,
                        ledger_index: 'validated'
                      });
                    } catch (error: unknown) {
                      const maybe = error as { data?: { error?: string } };
                      if (maybe?.data?.error === 'actNotFound') {
                        destExists = false;
                      } else {
                        throw error;
                      }
                    }

                    const senderLines = getAccountLinesResult(
                      await client.request({ command: 'account_lines', account: senderAddress, ledger_index: 'validated' })
                    );
                    const destLines = destExists
                      ? getAccountLinesResult(
                        await client.request({ command: 'account_lines', account: iouDestination, ledger_index: 'validated' })
                      )
                      : null;

                    setIouSenderLines(senderLines);
                    setIouDestLines(destLines);

                    const senderHasLine = hasTrustLine(senderLines, iouCurrency, iouIssuer);
                    const destHasLine = destExists ? hasTrustLine(destLines, iouCurrency, iouIssuer) : false;
                    setIouPreflight({ senderHasLine, destHasLine, destExists });

                    alert(
                      !destExists
                        ? 'Destination account does not exist on validated ledger. It must be activated with XRP before it can receive IOUs.'
                        : `Preflight complete.\n\nSender trust line: ${senderHasLine ? 'YES' : 'NO'}\nDestination trust line: ${destHasLine ? 'YES' : 'NO'}`
                    );
                  } catch (error) {
                    console.error('IOU preflight error:', error);
                    alert(`IOU preflight failed: ${formatErrorMessage(error)}`);
                  } finally {
                    try {
                      await client.disconnect();
                    } catch {
                      // ignore
                    }
                    setIouChecking(false);
                  }
                }}
                style={{
                  padding: '14px 18px',
                  fontSize: '13px',
                  fontWeight: 800,
                  background: iouChecking ? 'rgba(59, 130, 246, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '10px',
                  color: iouChecking ? '#9ca3af' : '#ffffff',
                  cursor: iouChecking ? 'not-allowed' : 'pointer',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                {iouChecking ? 'Checking…' : 'Preflight Trust Lines'}
              </button>

              <button
                disabled={!txArmed || iouSubmitting || !iouDestination || !iouAmount || !iouIssuer || (useXumm ? !xummAccount : !userWallet?.seed)}
                onClick={async () => {
                  if (!ensureTxArmedOrAlert('IOU Payment')) return;
                  const senderAddress = useXumm ? xummAccount : userWallet?.address;
                  if (!senderAddress) {
                    alert(useXumm ? 'Connect Xaman first so we have a sender address.' : 'Create a free account first so we have a sender address.');
                    return;
                  }
                  if (!useXumm && !userWallet?.seed) {
                    alert('No sender seed available. Create a free account (wallet) first so the app can sign transactions.');
                    return;
                  }
                  const amountNum = Number(iouAmount);
                  if (!Number.isFinite(amountNum) || amountNum <= 0) {
                    alert('Enter a valid IOU amount.');
                    return;
                  }
                  const tagNum = iouDestinationTag ? Number(iouDestinationTag) : undefined;
                  if (iouDestinationTag && (!Number.isInteger(tagNum) || (tagNum as number) < 0)) {
                    alert('Destination tag must be a non-negative integer.');
                    return;
                  }

                  const ok = window.confirm(
                    `Submit an IOU payment on XRPL MAINNET?\n\nFrom: ${senderAddress}\nTo: ${iouDestination}\nAmount: ${iouAmount} ${iouCurrency}\nIssuer: ${iouIssuer}${tagNum !== undefined ? `\nTag: ${tagNum}` : ''}`
                  );
                  if (!ok) return;

                  setIouSubmitting(true);
                  setIouReceipt(null);

                  const client = new Client('wss://xrplcluster.com');
                  try {
                    await client.connect();

                    const issuedAmount: IssuedAmount = {
                      currency: iouCurrency,
                      issuer: iouIssuer,
                      value: String(iouAmount)
                    };

                    const baseTx: Payment = {
                      TransactionType: 'Payment',
                      Account: senderAddress,
                      Destination: iouDestination,
                      Amount: issuedAmount as unknown as Payment['Amount']
                    };

                    const tx: Payment = {
                      ...baseTx,
                      ...(tagNum !== undefined ? { DestinationTag: tagNum as number } : {})
                    };

                    if (iouMemo.trim()) {
                      (tx as unknown as Record<string, unknown>).Memos = [
                        { Memo: { MemoData: utf8ToHex(iouMemo.trim()) } }
                      ];
                    }

                    const prepared = await client.autofill(tx);
                    setIouPreparedTx(prepared as unknown as Record<string, unknown>);

                    if (useXumm) {
                      const { txid } = await xummSubmit(prepared as unknown as Record<string, unknown>, `IOU Payment ${iouCurrency}`);
                      if (!txid) throw new Error('No txid returned from Xaman.');

                      const validatedResult = await waitForValidatedTx(client, txid);
                      const metaObj = (validatedResult && typeof validatedResult.meta === 'object' && validatedResult.meta !== null)
                        ? (validatedResult.meta as Record<string, unknown>)
                        : null;
                      const txResultFromMeta = metaObj && typeof metaObj.TransactionResult === 'string'
                        ? metaObj.TransactionResult
                        : undefined;
                      const txResultFromEngine = validatedResult && typeof validatedResult.engine_result === 'string'
                        ? validatedResult.engine_result
                        : undefined;
                      const txResult = txResultFromMeta || txResultFromEngine;

                      const ledgerIndexRaw = validatedResult?.ledger_index;
                      const ledgerIndex = typeof ledgerIndexRaw === 'number' ? ledgerIndexRaw : undefined;
                      const validated = validatedResult?.validated === true;

                      setIouReceipt({ hash: txid, validated, ledgerIndex, transactionResult: txResult });

                      alert(
                        `✓ IOU PAYMENT SUBMITTED (Xaman)\n\nResult: ${txResult || 'submitted'}\nValidated: ${validated ? 'YES' : 'NO/UNKNOWN'}\n\nTransaction: ${txid}\nExplorer:\nhttps://livenet.xrpl.org/transactions/${txid}`
                      );
                    } else {
                      const wallet = Wallet.fromSeed(userWallet!.seed);
                      const signed = wallet.sign(prepared);

                      const submitResp = await client.submitAndWait(signed.tx_blob);
                      const submitRespUnknown: unknown = submitResp;
                      const submitObj = (typeof submitRespUnknown === 'object' && submitRespUnknown !== null)
                        ? (submitRespUnknown as Record<string, unknown>)
                        : {};
                      const resultObj = (typeof submitObj.result === 'object' && submitObj.result !== null)
                        ? (submitObj.result as Record<string, unknown>)
                        : null;

                      const metaObj = (resultObj && typeof resultObj.meta === 'object' && resultObj.meta !== null)
                        ? (resultObj.meta as Record<string, unknown>)
                        : null;
                      const txResultFromMeta = metaObj && typeof metaObj.TransactionResult === 'string'
                        ? metaObj.TransactionResult
                        : undefined;
                      const txResultFromEngine = resultObj && typeof resultObj.engine_result === 'string'
                        ? resultObj.engine_result
                        : undefined;
                      const txResult = txResultFromMeta || txResultFromEngine;

                      const ledgerIndexRaw = resultObj?.ledger_index;
                      const ledgerIndex = typeof ledgerIndexRaw === 'number' ? ledgerIndexRaw : undefined;
                      const validated = resultObj?.validated === true;

                      setIouReceipt({
                        hash: signed.hash,
                        validated,
                        ledgerIndex,
                        transactionResult: txResult
                      });

                      alert(
                        `✓ IOU PAYMENT SUBMITTED\n\nResult: ${txResult || 'submitted'}\nValidated: ${validated ? 'YES' : 'NO/UNKNOWN'}\n\nTransaction: ${signed.hash}\nExplorer:\nhttps://livenet.xrpl.org/transactions/${signed.hash}`
                      );
                    }
                  } catch (error) {
                    console.error('IOU Payment submit error:', error);
                    alert(`IOU Payment submit failed: ${formatErrorMessage(error)}`);
                  } finally {
                    try {
                      await client.disconnect();
                    } catch {
                      // ignore
                    }
                    setIouSubmitting(false);
                  }
                }}
                style={{
                  padding: '14px 18px',
                  fontSize: '13px',
                  fontWeight: 900,
                  background: (!txArmed || iouSubmitting || !iouDestination || !iouAmount || !iouIssuer || (useXumm ? !xummAccount : !userWallet?.seed)) ? 'rgba(59, 130, 246, 0.25)' : '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  color: (!txArmed || iouSubmitting || !iouDestination || !iouAmount || !iouIssuer || (useXumm ? !xummAccount : !userWallet?.seed)) ? '#9ca3af' : '#000000',
                  cursor: (!txArmed || iouSubmitting || !iouDestination || !iouAmount || !iouIssuer || (useXumm ? !xummAccount : !userWallet?.seed)) ? 'not-allowed' : 'pointer',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                {iouSubmitting ? 'Submitting…' : 'Sign & Submit IOU Payment'}
              </button>

              {iouPreparedTx && (
                <button
                  onClick={() => {
                    const fromAddress = useXumm ? xummAccount : userWallet?.address;
                    downloadJson(`uny-iou-payment-${fromAddress || 'sender'}.json`, iouPreparedTx);
                  }}
                  style={{
                    padding: '14px 18px',
                    fontSize: '13px',
                    fontWeight: 800,
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  Download TX JSON
                </button>
              )}

              {iouReceipt && (
                <button
                  onClick={() => {
                    const fromAddress = useXumm ? xummAccount : userWallet?.address;
                    const proof = {
                      type: 'XRPL_IOU_PAYMENT_RECEIPT',
                      network: 'XRPL Mainnet',
                      websocket: 'wss://xrplcluster.com',
                      createdAt: new Date().toISOString(),
                      from: fromAddress,
                      to: iouDestination,
                      amount: iouAmount,
                      currency: iouCurrency,
                      issuer: iouIssuer,
                      destinationTag: iouDestinationTag ? Number(iouDestinationTag) : undefined,
                      memo: iouMemo.trim() ? iouMemo.trim() : undefined,
                      txHash: iouReceipt.hash,
                      validated: iouReceipt.validated,
                      ledgerIndex: iouReceipt.ledgerIndex,
                      transactionResult: iouReceipt.transactionResult,
                      preparedTx: iouPreparedTx,
                      preflight: iouPreflight,
                      postConditions: {
                        senderAccountLines: iouSenderLines,
                        destinationAccountLines: iouDestLines
                      }
                    };
                    downloadJson(`uny-iou-settlement-proof-${iouReceipt.hash}.json`, proof);
                  }}
                  style={{
                    padding: '14px 18px',
                    fontSize: '13px',
                    fontWeight: 900,
                    background: '#60a5fa',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#000000',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  Download Proof
                </button>
              )}
            </div>

            {iouPreflight && (
              <div style={{
                marginTop: '14px',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(59, 130, 246, 0.18)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Preflight Summary
                </div>
                <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: 1.7 }}>
                  <div><strong style={{ color: '#93c5fd' }}>Destination exists:</strong> {iouPreflight.destExists ? 'YES' : 'NO (needs XRP activation)'}</div>
                  <div><strong style={{ color: '#93c5fd' }}>Sender trust line:</strong> {iouPreflight.senderHasLine ? 'YES' : 'NO'}</div>
                  <div><strong style={{ color: '#93c5fd' }}>Destination trust line:</strong> {iouPreflight.destHasLine ? 'YES' : 'NO'}</div>
                </div>
              </div>
            )}

            {iouReceipt && (
              <div style={{
                marginTop: '14px',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(59, 130, 246, 0.18)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Latest IOU Receipt
                </div>
                <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: 1.7 }}>
                  <div><strong style={{ color: '#60a5fa' }}>Hash:</strong> <span style={{ fontFamily: 'monospace' }}>{iouReceipt.hash}</span></div>
                  <div><strong style={{ color: '#60a5fa' }}>Result:</strong> {iouReceipt.transactionResult || 'submitted'}</div>
                  <div><strong style={{ color: '#60a5fa' }}>Validated:</strong> {iouReceipt.validated ? 'YES' : 'NO/UNKNOWN'}</div>
                  <XummLink
                    href={`https://livenet.xrpl.org/transactions/${iouReceipt.hash}`}
                    style={{ color: '#93c5fd', textDecoration: 'none', fontSize: '13px', display: 'inline-block', marginTop: '6px' }}
                  >
                    → View on XRPL Explorer
                  </XummLink>
                </div>
              </div>
            )}
          </div>

          {/* Trustline Builder + Asset Registry */}
          <EvidenceVault
            isMobile={isMobile}
            onSetXrpMemo={(memo) => setSettlementMemo(memo)}
            onSetIouMemo={(memo) => setIouMemo(memo)}
            onSetPathMemo={(memo) => setPathMemo(memo)}
          />

          <TrustlineManager
            sender={useXumm ? (xummAccount ? { address: xummAccount } : null) : userWallet}
            isMobile={isMobile}
            txArmed={txArmed}
            xummSubmit={useXumm ? xummSubmit : undefined}
          />

          {/* Routed Settlement (Path Payments) */}
          <div style={{
            padding: isMobile ? '22px' : '28px',
            background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.14) 0%, rgba(15, 23, 42, 0.25) 100%)',
            border: '1px solid rgba(168, 85, 247, 0.22)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#c4b5fd', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 800 }}>
                  Routed Settlement
                </div>
                <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 600, color: '#ffffff', marginTop: '8px' }}>
                  Path Payments (DEX routing)
                </div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.6, maxWidth: '980px' }}>
                  This uses <span style={{ color: '#ddd6fe', fontWeight: 700 }}>ripple_path_find</span> to quote routes, then submits a Payment with Paths + SendMax (slippage cap) + optional DeliverMin.
                </div>
              </div>

              <div style={{
                padding: '12px 14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>
                  Sender
                </div>
                <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#e5e7eb', wordBreak: 'break-all' }}>
                  {useXumm
                    ? (xummAccount || 'Not connected (Connect Xaman above)')
                    : (userWallet?.address || 'Not set (create/free account)')}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr', gap: '16px', marginTop: '18px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Destination</div>
                <input
                  value={pathDest}
                  onChange={(e) => setPathDest(e.target.value.trim())}
                  placeholder="r..."
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(168, 85, 247, 0.22)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Destination Tag (optional)</div>
                <input
                  value={pathDestTag}
                  onChange={(e) => setPathDestTag(e.target.value)}
                  placeholder="12345"
                  inputMode="numeric"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>
            </div>

            {/* Quick Swap */}
            <div style={{
              marginTop: '14px',
              padding: '14px',
              background: 'rgba(0, 0, 0, 0.32)',
              border: '1px solid rgba(255, 255, 255, 0.10)',
              borderRadius: '14px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#ddd6fe', fontWeight: 900, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                    Quick Swap
                  </div>
                  <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px', lineHeight: 1.6 }}>
                    Set what the recipient should receive, then auto-quote and auto-fill <strong>SendMax</strong> using your slippage cap.
                  </div>
                </div>

                <div style={{
                  padding: '10px 12px',
                  background: 'rgba(0, 0, 0, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.10)',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>Slippage cap (%)</div>
                  <input
                    value={pathSlippagePct}
                    onChange={(e) => setPathSlippagePct(e.target.value)}
                    inputMode="decimal"
                    placeholder="1.0"
                    style={{
                      width: '140px',
                      padding: '10px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: '10px',
                      color: '#ffffff',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '14px' }}>
                <button
                  disabled={pathFinding || !pathDest || !pathDeliverAmount || (useXumm ? !xummAccount : !userWallet?.address)}
                  onClick={async () => {
                    const senderAddress = useXumm ? xummAccount : userWallet?.address;
                    if (!senderAddress) {
                      alert(useXumm ? 'Connect Xaman first so we have a sender address.' : 'Create a free account first so we have a sender address.');
                      return;
                    }
                    if (!isProbablyXrplClassicAddress(pathDest)) {
                      alert('Destination address does not look like a classic XRPL address.');
                      return;
                    }
                    if (!pathDeliverAmount) {
                      alert('Enter a deliver amount.');
                      return;
                    }
                    if (pathDeliverCurrency !== 'XRP' && !pathDeliverIssuer) {
                      alert('Deliver issuer is required for IOUs.');
                      return;
                    }

                    // For quick mode, default SendMax currency to XRP (common swap UX)
                    if (pathSendCurrency !== 'XRP') {
                      setPathSendCurrency('XRP');
                    }

                    const slip = Number(pathSlippagePct);
                    if (!Number.isFinite(slip) || slip < 0) {
                      alert('Slippage must be a non-negative number.');
                      return;
                    }

                    setPathFinding(true);
                    setPathReceipt(null);
                    setPathPreparedTx(null);
                    setPathSubmitResult(null);
                    setPathAlternatives([]);
                    setPathSelectedAltIndex(0);
                    setPathQuoteResult(null);

                    const client = new Client('wss://xrplcluster.com');
                    try {
                      await client.connect();

                      const destinationAmount = toXrplAmount(
                        pathDeliverCurrency,
                        pathDeliverCurrency === 'XRP' ? '' : pathDeliverIssuer,
                        pathDeliverAmount
                      );

                      const sourceCurrencies: Array<Record<string, unknown>> = [
                        { currency: 'XRP' }
                      ];

                      const req: Record<string, unknown> = {
                        command: 'ripple_path_find',
                        source_account: senderAddress,
                        destination_account: pathDest,
                        destination_amount: destinationAmount,
                        source_currencies: sourceCurrencies
                      };
                      setPathQuoteRequest(req);

                      const respUnknown: unknown = await client.request(req as unknown as never);
                      const respObj = (typeof respUnknown === 'object' && respUnknown !== null)
                        ? (respUnknown as Record<string, unknown>)
                        : null;
                      const resultObj = (respObj && typeof respObj.result === 'object' && respObj.result !== null)
                        ? (respObj.result as Record<string, unknown>)
                        : null;
                      setPathQuoteResult(resultObj);

                      const alts = (resultObj && Array.isArray(resultObj.alternatives))
                        ? (resultObj.alternatives as Array<unknown>)
                        : [];
                      const altRecords = alts
                        .filter((a) => typeof a === 'object' && a !== null)
                        .slice(0, 5)
                        .map((a) => a as Record<string, unknown>);
                      setPathAlternatives(altRecords);

                      const bestAlt = altRecords[0];
                      const sourceAmount = bestAlt ? (bestAlt.source_amount as unknown) : undefined;
                      const sourceAmountTyped: XRPLAmount | null =
                        typeof sourceAmount === 'string'
                          ? sourceAmount
                          : (typeof sourceAmount === 'object' && sourceAmount !== null)
                            ? (sourceAmount as IssuedAmount)
                            : null;

                      if (!sourceAmountTyped) {
                        alert(
                          altRecords.length
                            ? 'Found route alternatives, but could not parse source_amount from the best route.'
                            : 'No routes found. Try a different deliver amount or asset.'
                        );
                        return;
                      }

                      const sendMaxWithSlippage = applySlippage(sourceAmountTyped, slip);

                      // For XRP send max, store as XRP amount (not drops)
                      if (typeof sendMaxWithSlippage === 'string') {
                        const drops = Number(sendMaxWithSlippage);
                        const xrp = Number.isFinite(drops) ? drops / 1_000_000 : 0;
                        setPathSendMax(String(xrp));
                        setPathSendCurrency('XRP');
                      } else {
                        setPathSendMax(sendMaxWithSlippage.value);
                        setPathSendCurrency(sendMaxWithSlippage.currency as CurrencyKey);
                        setPathSendIssuer(sendMaxWithSlippage.issuer);
                      }

                      alert(
                        altRecords.length
                          ? `✓ Quoted ${altRecords.length} route option(s).\n\nEstimated cost (best route): ${formatXrplAmount(sourceAmountTyped)}\nSendMax (with ${slip}% slippage): ${formatXrplAmount(sendMaxWithSlippage)}`
                          : 'No routes found. Try adjusting the deliver amount or asset pair.'
                      );
                    } catch (error) {
                      console.error('Quick swap quote error:', error);
                      alert(`Quick swap quote failed: ${formatErrorMessage(error)}`);
                    } finally {
                      try { await client.disconnect(); } catch { /* ignore */ }
                      setPathFinding(false);
                    }
                  }}
                  style={{
                    padding: '12px 14px',
                    fontSize: '12px',
                    fontWeight: 900,
                    background: pathFinding ? 'rgba(168, 85, 247, 0.25)' : '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    color: pathFinding ? '#9ca3af' : '#000000',
                    cursor: pathFinding ? 'not-allowed' : 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  {pathFinding ? 'Quoting…' : 'Quick Quote & Set SendMax'}
                </button>

                <button
                  onClick={() => {
                    setPathSendCurrency('XRP');
                    setPathSendIssuer('rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm');
                    setPathSendMax('');
                    setPathAlternatives([]);
                    setPathQuoteResult(null);
                    setPathQuoteRequest(null);
                    setPathSelectedAltIndex(0);
                  }}
                  style={{
                    padding: '12px 14px',
                    fontSize: '12px',
                    fontWeight: 800,
                    background: 'rgba(255, 255, 255, 0.06)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  Reset Quote
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginTop: '14px' }}>
              <div style={{
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#ddd6fe', fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                  Deliver (what recipient gets)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 1.3fr', gap: '12px', marginTop: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Currency</div>
                    <select
                      value={pathDeliverCurrency}
                      onChange={(e) => setPathDeliverCurrency(e.target.value as CurrencyKey)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                        color: '#e5e7eb'
                      }}
                    >
                      <option value="XRP">XRP</option>
                      <option value="USDT">USDT</option>
                      <option value="USD">USD</option>
                      <option value="RLUSD">RLUSD</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Issuer (required for IOUs)</div>
                    <input
                      value={pathDeliverIssuer}
                      onChange={(e) => setPathDeliverIssuer(e.target.value.trim())}
                      placeholder="rIssuer..."
                      disabled={pathDeliverCurrency === 'XRP'}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        fontFamily: 'monospace',
                        opacity: pathDeliverCurrency === 'XRP' ? 0.6 : 1
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Amount</div>
                    <input
                      value={pathDeliverAmount}
                      onChange={(e) => setPathDeliverAmount(e.target.value)}
                      placeholder={pathDeliverCurrency === 'XRP' ? '10' : '1000'}
                      inputMode="decimal"
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(168, 85, 247, 0.22)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        fontFamily: 'monospace'
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>DeliverMin (optional)</div>
                    <input
                      value={pathDeliverMin}
                      onChange={(e) => setPathDeliverMin(e.target.value)}
                      placeholder={pathDeliverCurrency === 'XRP' ? '9.9' : '990'}
                      inputMode="decimal"
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        fontFamily: 'monospace'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.10)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#ddd6fe', fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                  SendMax (slippage cap)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 1.3fr', gap: '12px', marginTop: '12px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Currency</div>
                    <select
                      value={pathSendCurrency}
                      onChange={(e) => setPathSendCurrency(e.target.value as CurrencyKey)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                        color: '#e5e7eb'
                      }}
                    >
                      <option value="XRP">XRP</option>
                      <option value="USDT">USDT</option>
                      <option value="USD">USD</option>
                      <option value="RLUSD">RLUSD</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Issuer (required for IOUs)</div>
                    <input
                      value={pathSendIssuer}
                      onChange={(e) => setPathSendIssuer(e.target.value.trim())}
                      placeholder="rIssuer..."
                      disabled={pathSendCurrency === 'XRP'}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        fontFamily: 'monospace',
                        opacity: pathSendCurrency === 'XRP' ? 0.6 : 1
                      }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Max Amount</div>
                  <input
                    value={pathSendMax}
                    onChange={(e) => setPathSendMax(e.target.value)}
                    placeholder={pathSendCurrency === 'XRP' ? '10.5' : '1100'}
                    inputMode="decimal"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(0, 0, 0, 0.4)',
                      border: '1px solid rgba(168, 85, 247, 0.22)',
                      borderRadius: '10px',
                      color: '#ffffff',
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginTop: '14px' }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Memo (optional)</div>
              <input
                value={pathMemo}
                onChange={(e) => setPathMemo(e.target.value)}
                placeholder="Invoice #123 / Reference"
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: '10px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
              <button
                disabled={pathPreflightChecking || !pathDest || (useXumm ? !xummAccount : !userWallet?.address)}
                onClick={async () => {
                  const senderAddress = useXumm ? xummAccount : userWallet?.address;
                  if (!senderAddress) {
                    alert(useXumm ? 'Connect Xaman first so we have a sender address.' : 'Create a free account first so we have a sender address.');
                    return;
                  }
                  if (!isProbablyXrplClassicAddress(pathDest)) {
                    alert('Destination address does not look like a classic XRPL address.');
                    return;
                  }
                  if (pathDeliverCurrency !== 'XRP' && !pathDeliverIssuer) {
                    alert('Deliver issuer is required for IOUs.');
                    return;
                  }
                  if (pathSendCurrency !== 'XRP' && !pathSendIssuer) {
                    alert('Send issuer is required for IOUs.');
                    return;
                  }

                  setPathPreflightChecking(true);
                  setPathDestAccountInfo(null);
                  setPathSenderLines(null);
                  setPathDestLines(null);
                  setPathPreflight(null);

                  const client = new Client('wss://xrplcluster.com');
                  try {
                    await client.connect();

                    // Destination existence + flags
                    let destExists = true;
                    let destRequiresTag = false;
                    let destDepositAuth = false;
                    let destInfoResult: Record<string, unknown> | null = null;

                    try {
                      const destInfoUnknown: unknown = await client.request({
                        command: 'account_info',
                        account: pathDest,
                        ledger_index: 'validated'
                      });
                      const destInfoObj = (typeof destInfoUnknown === 'object' && destInfoUnknown !== null)
                        ? (destInfoUnknown as Record<string, unknown>)
                        : null;
                      destInfoResult = (destInfoObj && typeof destInfoObj.result === 'object' && destInfoObj.result !== null)
                        ? (destInfoObj.result as Record<string, unknown>)
                        : null;

                      const accountData = (destInfoResult && typeof destInfoResult.account_data === 'object' && destInfoResult.account_data !== null)
                        ? (destInfoResult.account_data as Record<string, unknown>)
                        : null;
                      const flags = accountData && typeof accountData.Flags === 'number' ? accountData.Flags : 0;

                      // XRPL AccountRoot flags (bitmasks)
                      // lsfRequireDestTag = 0x00010000
                      // lsfDepositAuth     = 0x01000000
                      destRequiresTag = (flags & 0x00010000) !== 0;
                      destDepositAuth = (flags & 0x01000000) !== 0;
                    } catch (error: unknown) {
                      const maybe = error as { data?: { error?: string } };
                      if (maybe?.data?.error === 'actNotFound') {
                        destExists = false;
                      } else {
                        throw error;
                      }
                    }

                    setPathDestAccountInfo(destInfoResult);

                    const senderLines = (pathSendCurrency === 'XRP')
                      ? null
                      : getAccountLinesResult(
                        await client.request({ command: 'account_lines', account: senderAddress, ledger_index: 'validated' })
                      );
                    const destLines = (!destExists || pathDeliverCurrency === 'XRP')
                      ? null
                      : getAccountLinesResult(
                        await client.request({ command: 'account_lines', account: pathDest, ledger_index: 'validated' })
                      );

                    setPathSenderLines(senderLines);
                    setPathDestLines(destLines);

                    const senderHasSendLine = pathSendCurrency === 'XRP'
                      ? true
                      : hasTrustLine(senderLines, pathSendCurrency, pathSendIssuer);
                    const destHasDeliverLine = pathDeliverCurrency === 'XRP'
                      ? true
                      : (destExists ? hasTrustLine(destLines, pathDeliverCurrency, pathDeliverIssuer) : false);

                    setPathPreflight({ destExists, destRequiresTag, destDepositAuth, senderHasSendLine, destHasDeliverLine });

                    const lines: string[] = [];
                    lines.push(`Destination exists: ${destExists ? 'YES' : 'NO (needs XRP activation)'}`);
                    lines.push(`Destination requires tag: ${destRequiresTag ? 'YES' : 'NO'}`);
                    lines.push(`Destination DepositAuth: ${destDepositAuth ? 'YES (incoming restricted)' : 'NO'}`);
                    lines.push(`Sender trust line for SendMax: ${senderHasSendLine ? 'YES' : 'NO'}`);
                    lines.push(`Destination trust line for Deliver: ${destHasDeliverLine ? 'YES' : 'NO'}`);
                    alert(`Path Payment preflight complete.\n\n${lines.join('\n')}`);
                  } catch (error) {
                    console.error('Path preflight error:', error);
                    alert(`Path preflight failed: ${formatErrorMessage(error)}`);
                  } finally {
                    try { await client.disconnect(); } catch { /* ignore */ }
                    setPathPreflightChecking(false);
                  }
                }}
                style={{
                  padding: '14px 18px',
                  fontSize: '13px',
                  fontWeight: 800,
                  background: pathPreflightChecking ? 'rgba(168, 85, 247, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '10px',
                  color: pathPreflightChecking ? '#9ca3af' : '#ffffff',
                  cursor: pathPreflightChecking ? 'not-allowed' : 'pointer',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                {pathPreflightChecking ? 'Checking…' : 'Preflight Checks'}
              </button>

              <button
                disabled={pathFinding || !pathDest || !pathDeliverAmount || (useXumm ? !xummAccount : !userWallet?.address)}
                onClick={async () => {
                  const senderAddress = useXumm ? xummAccount : userWallet?.address;
                  if (!senderAddress) {
                    alert(useXumm ? 'Connect Xaman first so we have a sender address.' : 'Create a free account first so we have a sender address.');
                    return;
                  }
                  if (!isProbablyXrplClassicAddress(pathDest)) {
                    alert('Destination address does not look like a classic XRPL address.');
                    return;
                  }
                  if (!pathDeliverAmount) {
                    alert('Enter a deliver amount.');
                    return;
                  }
                  if (pathDeliverCurrency !== 'XRP' && !pathDeliverIssuer) {
                    alert('Deliver issuer is required for IOUs.');
                    return;
                  }
                  if (pathSendCurrency !== 'XRP' && !pathSendIssuer) {
                    alert('Send issuer is required for IOUs.');
                    return;
                  }
                  if (!pathSendMax) {
                    alert('SendMax is required to cap slippage.');
                    return;
                  }

                  setPathFinding(true);
                  setPathReceipt(null);
                  setPathPreparedTx(null);
                  setPathSubmitResult(null);
                  setPathAlternatives([]);
                  setPathSelectedAltIndex(0);
                  setPathQuoteResult(null);

                  const client = new Client('wss://xrplcluster.com');
                  try {
                    await client.connect();

                    const destinationAmount = toXrplAmount(
                      pathDeliverCurrency,
                      pathDeliverCurrency === 'XRP' ? '' : pathDeliverIssuer,
                      pathDeliverAmount
                    );
                    const sendMaxAmount = toXrplAmount(
                      pathSendCurrency,
                      pathSendCurrency === 'XRP' ? '' : pathSendIssuer,
                      pathSendMax
                    );

                    const req: Record<string, unknown> = {
                      command: 'ripple_path_find',
                      source_account: senderAddress,
                      destination_account: pathDest,
                      destination_amount: destinationAmount,
                      send_max: sendMaxAmount
                    };
                    setPathQuoteRequest(req);

                    const respUnknown: unknown = await client.request(req as unknown as never);
                    const respObj = (typeof respUnknown === 'object' && respUnknown !== null)
                      ? (respUnknown as Record<string, unknown>)
                      : null;
                    const resultObj = (respObj && typeof respObj.result === 'object' && respObj.result !== null)
                      ? (respObj.result as Record<string, unknown>)
                      : null;
                    setPathQuoteResult(resultObj);

                    const alts = (resultObj && Array.isArray(resultObj.alternatives))
                      ? (resultObj.alternatives as Array<unknown>)
                      : [];
                    const altRecords = alts
                      .filter((a) => typeof a === 'object' && a !== null)
                      .slice(0, 5)
                      .map((a) => a as Record<string, unknown>);
                    setPathAlternatives(altRecords);

                    alert(
                      altRecords.length
                        ? `✓ Found ${altRecords.length} route option(s). Select one and submit.`
                        : 'No routes found for that deliver amount / send max. Try adjusting SendMax or asset pairs.'
                    );
                  } catch (error) {
                    console.error('Path find error:', error);
                    alert(`Path find failed: ${formatErrorMessage(error)}`);
                  } finally {
                    try { await client.disconnect(); } catch { /* ignore */ }
                    setPathFinding(false);
                  }
                }}
                style={{
                  padding: '14px 18px',
                  fontSize: '13px',
                  fontWeight: 900,
                  background: pathFinding ? 'rgba(168, 85, 247, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '10px',
                  color: pathFinding ? '#9ca3af' : '#ffffff',
                  cursor: pathFinding ? 'not-allowed' : 'pointer',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                {pathFinding ? 'Finding…' : 'Find Routes (ripple_path_find)'}
              </button>

              <button
                disabled={!txArmed || pathSubmitting || !pathDest || pathAlternatives.length === 0 || (useXumm ? !xummAccount : !userWallet?.seed)}
                onClick={async () => {
                  if (!ensureTxArmedOrAlert('Path Payment')) return;
                  const senderAddress = useXumm ? xummAccount : userWallet?.address;
                  if (!senderAddress) {
                    alert(useXumm ? 'Connect Xaman first so we have a sender address.' : 'Create a free account first so we have a sender address.');
                    return;
                  }
                  if (!useXumm && !userWallet?.seed) {
                    alert('No sender seed available. Create a free account (wallet) first so the app can sign transactions.');
                    return;
                  }
                  if (pathPreflight && !pathPreflight.destExists) {
                    alert('Destination account does not exist on validated ledger. Activate it with XRP before attempting path payments (especially for IOUs).');
                    return;
                  }
                  if (pathPreflight && pathPreflight.destRequiresTag && !pathDestTag) {
                    alert('Destination account requires a Destination Tag. Provide one before submitting.');
                    return;
                  }
                  if (pathPreflight && pathPreflight.destDepositAuth) {
                    const okDepositAuth = window.confirm(
                      'WARNING: Destination has DepositAuth enabled (incoming payments are restricted unless you are preauthorized).\n\nProceed anyway?'
                    );
                    if (!okDepositAuth) return;
                  }
                  if (pathPreflight && !pathPreflight.senderHasSendLine) {
                    alert('Sender is missing the trust line required for the SendMax asset. Create the trust line (TrustSet) first.');
                    return;
                  }
                  if (pathPreflight && !pathPreflight.destHasDeliverLine) {
                    alert('Destination is missing the trust line required for the Deliver asset. The recipient must set a trust line first.');
                    return;
                  }
                  if (!pathPreflight) {
                    const okNoPreflight = window.confirm(
                      'You have not run Preflight Checks for this path payment.\n\nSubmitting without preflight may fail (missing trust lines, destination tag required, DepositAuth, etc).\n\nProceed anyway?'
                    );
                    if (!okNoPreflight) return;
                  }
                  if (pathAlternatives.length === 0) {
                    alert('No route alternatives available. Run Find Routes first.');
                    return;
                  }
                  const alt = pathAlternatives[Math.min(pathSelectedAltIndex, pathAlternatives.length - 1)];
                  const pathsComputed = alt.paths_computed;
                  if (!pathsComputed) {
                    alert('Selected alternative does not include paths_computed. Try another alternative.');
                    return;
                  }

                  const tagNum = pathDestTag ? Number(pathDestTag) : undefined;
                  if (pathDestTag && (!Number.isInteger(tagNum) || (tagNum as number) < 0)) {
                    alert('Destination tag must be a non-negative integer.');
                    return;
                  }

                  setPathSubmitting(true);
                  setPathReceipt(null);
                  setPathSubmitResult(null);

                  const client = new Client('wss://xrplcluster.com');
                  try {
                    await client.connect();

                    const destinationAmount = toXrplAmount(
                      pathDeliverCurrency,
                      pathDeliverCurrency === 'XRP' ? '' : pathDeliverIssuer,
                      pathDeliverAmount
                    );
                    const sendMaxAmount = toXrplAmount(
                      pathSendCurrency,
                      pathSendCurrency === 'XRP' ? '' : pathSendIssuer,
                      pathSendMax
                    );

                    const txObj: Record<string, unknown> = {
                      TransactionType: 'Payment',
                      Account: senderAddress,
                      Destination: pathDest,
                      Amount: destinationAmount,
                      SendMax: sendMaxAmount,
                      Paths: pathsComputed
                    };
                    if (tagNum !== undefined) txObj.DestinationTag = tagNum;

                    if (pathDeliverMin.trim()) {
                      const deliverMinAmount = toXrplAmount(
                        pathDeliverCurrency,
                        pathDeliverCurrency === 'XRP' ? '' : pathDeliverIssuer,
                        pathDeliverMin
                      );
                      txObj.DeliverMin = deliverMinAmount;
                    }

                    if (pathMemo.trim()) {
                      txObj.Memos = [{ Memo: { MemoData: utf8ToHex(pathMemo.trim()) } }];
                    }

                    const prepared = await client.autofill(txObj as unknown as Payment);
                    setPathPreparedTx(prepared as unknown as Record<string, unknown>);

                    if (useXumm) {
                      const { txid } = await xummSubmit(prepared as unknown as Record<string, unknown>, 'Path Payment');
                      if (!txid) throw new Error('No txid returned from Xaman.');

                      const validatedResult = await waitForValidatedTx(client, txid);
                      const metaObj = (validatedResult && typeof validatedResult.meta === 'object' && validatedResult.meta !== null)
                        ? (validatedResult.meta as Record<string, unknown>)
                        : null;
                      const txResultFromMeta = metaObj && typeof metaObj.TransactionResult === 'string'
                        ? metaObj.TransactionResult
                        : undefined;
                      const txResultFromEngine = validatedResult && typeof validatedResult.engine_result === 'string'
                        ? validatedResult.engine_result
                        : undefined;
                      const txResult = txResultFromMeta || txResultFromEngine;

                      const deliveredRaw = metaObj ? metaObj.delivered_amount : undefined;
                      const deliveredAmount: XRPLAmount | undefined =
                        typeof deliveredRaw === 'string'
                          ? deliveredRaw
                          : (typeof deliveredRaw === 'object' && deliveredRaw !== null)
                            ? (deliveredRaw as IssuedAmount)
                            : undefined;

                      const ledgerIndexRaw = validatedResult?.ledger_index;
                      const ledgerIndex = typeof ledgerIndexRaw === 'number' ? ledgerIndexRaw : undefined;
                      const validated = validatedResult?.validated === true;

                      setPathSubmitResult(validatedResult);
                      setPathReceipt({ hash: txid, validated, ledgerIndex, transactionResult: txResult, deliveredAmount });

                      alert(
                        `✓ PATH PAYMENT SUBMITTED (Xaman)\n\nResult: ${txResult || 'submitted'}\nValidated: ${validated ? 'YES' : 'NO/UNKNOWN'}\n\nTransaction: ${txid}\nExplorer:\nhttps://livenet.xrpl.org/transactions/${txid}`
                      );
                    } else {
                      const wallet = Wallet.fromSeed(userWallet!.seed);
                      const signed = wallet.sign(prepared);

                      const submitResp = await client.submitAndWait(signed.tx_blob);
                      const submitRespUnknown: unknown = submitResp;
                      const submitObj = (typeof submitRespUnknown === 'object' && submitRespUnknown !== null)
                        ? (submitRespUnknown as Record<string, unknown>)
                        : {};
                      const resultObj = (typeof submitObj.result === 'object' && submitObj.result !== null)
                        ? (submitObj.result as Record<string, unknown>)
                        : null;

                      const metaObj = (resultObj && typeof resultObj.meta === 'object' && resultObj.meta !== null)
                        ? (resultObj.meta as Record<string, unknown>)
                        : null;
                      const txResultFromMeta = metaObj && typeof metaObj.TransactionResult === 'string'
                        ? metaObj.TransactionResult
                        : undefined;
                      const txResultFromEngine = resultObj && typeof resultObj.engine_result === 'string'
                        ? resultObj.engine_result
                        : undefined;
                      const txResult = txResultFromMeta || txResultFromEngine;

                      const deliveredRaw = metaObj ? metaObj.delivered_amount : undefined;
                      const deliveredAmount: XRPLAmount | undefined =
                        typeof deliveredRaw === 'string'
                          ? deliveredRaw
                          : (typeof deliveredRaw === 'object' && deliveredRaw !== null)
                            ? (deliveredRaw as IssuedAmount)
                            : undefined;

                      const ledgerIndexRaw = resultObj?.ledger_index;
                      const ledgerIndex = typeof ledgerIndexRaw === 'number' ? ledgerIndexRaw : undefined;
                      const validated = resultObj?.validated === true;

                      setPathSubmitResult(resultObj);
                      setPathReceipt({ hash: signed.hash, validated, ledgerIndex, transactionResult: txResult, deliveredAmount });

                      alert(
                        `✓ PATH PAYMENT SUBMITTED\n\nResult: ${txResult || 'submitted'}\nValidated: ${validated ? 'YES' : 'NO/UNKNOWN'}\n\nTransaction: ${signed.hash}\nExplorer:\nhttps://livenet.xrpl.org/transactions/${signed.hash}`
                      );
                    }
                  } catch (error) {
                    console.error('Path payment submit error:', error);
                    alert(`Path payment submit failed: ${formatErrorMessage(error)}`);
                  } finally {
                    try { await client.disconnect(); } catch { /* ignore */ }
                    setPathSubmitting(false);
                  }
                }}
                style={{
                  padding: '14px 18px',
                  fontSize: '13px',
                  fontWeight: 900,
                  background: (!txArmed || pathSubmitting || !userWallet?.seed || !pathDest || pathAlternatives.length === 0) ? 'rgba(168, 85, 247, 0.25)' : '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  color: (!txArmed || pathSubmitting || !userWallet?.seed || !pathDest || pathAlternatives.length === 0) ? '#9ca3af' : '#000000',
                  cursor: (!txArmed || pathSubmitting || !userWallet?.seed || !pathDest || pathAlternatives.length === 0) ? 'not-allowed' : 'pointer',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase'
                }}
              >
                {pathSubmitting ? 'Submitting…' : 'Sign & Submit Path Payment'}
              </button>

              {pathPreparedTx && (
                <button
                  onClick={() => downloadJson(`uny-pathpayment-${userWallet?.address || 'sender'}.json`, pathPreparedTx)}
                  style={{
                    padding: '14px 18px',
                    fontSize: '13px',
                    fontWeight: 800,
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '10px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  Download TX JSON
                </button>
              )}

              {pathReceipt && (
                <button
                  onClick={() => {
                    const selectedAlt = pathAlternatives[Math.min(pathSelectedAltIndex, Math.max(0, pathAlternatives.length - 1))];
                    const fromAddress = useXumm ? xummAccount : userWallet?.address;
                    const proof = {
                      type: 'XRPL_PATH_PAYMENT_PROOF',
                      network: 'XRPL Mainnet',
                      websocket: 'wss://xrplcluster.com',
                      createdAt: new Date().toISOString(),
                      from: fromAddress,
                      to: pathDest,
                      deliver: {
                        amount: pathDeliverAmount,
                        currency: pathDeliverCurrency,
                        issuer: pathDeliverCurrency === 'XRP' ? undefined : pathDeliverIssuer,
                        deliverMin: pathDeliverMin || undefined
                      },
                      sendMax: {
                        amount: pathSendMax,
                        currency: pathSendCurrency,
                        issuer: pathSendCurrency === 'XRP' ? undefined : pathSendIssuer
                      },
                      destinationTag: pathDestTag ? Number(pathDestTag) : undefined,
                      memo: pathMemo.trim() ? pathMemo.trim() : undefined,
                      quote: {
                        request: pathQuoteRequest,
                        result: pathQuoteResult,
                        selectedAlternativeIndex: pathSelectedAltIndex,
                        selectedAlternative: selectedAlt
                      },
                      preflight: pathPreflight,
                      preflightSnapshots: {
                        destinationAccountInfo: pathDestAccountInfo,
                        senderAccountLines: pathSenderLines,
                        destinationAccountLines: pathDestLines
                      },
                      tx: {
                        prepared: pathPreparedTx,
                        submitResult: pathSubmitResult,
                        txHash: pathReceipt.hash,
                        validated: pathReceipt.validated,
                        ledgerIndex: pathReceipt.ledgerIndex,
                        transactionResult: pathReceipt.transactionResult,
                        deliveredAmount: pathReceipt.deliveredAmount
                      }
                    };
                    downloadJson(`uny-pathpayment-proof-${pathReceipt.hash}.json`, proof);
                  }}
                  style={{
                    padding: '14px 18px',
                    fontSize: '13px',
                    fontWeight: 900,
                    background: '#a78bfa',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#000000',
                    cursor: 'pointer',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  Download Proof
                </button>
              )}
            </div>

            {pathAlternatives.length > 0 && (
              <div style={{
                marginTop: '14px',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(168, 85, 247, 0.18)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Route Options
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {pathAlternatives.map((alt, idx) => {
                    const sourceAmount = alt.source_amount as unknown;
                    const sourceLabel = (typeof sourceAmount === 'string' || (typeof sourceAmount === 'object' && sourceAmount !== null))
                      ? (() => {
                        try {
                          return formatXrplAmount(sourceAmount as XRPLAmount);
                        } catch {
                          return 'source_amount';
                        }
                      })()
                      : 'source_amount';
                    return (
                      <button
                        key={idx}
                        onClick={() => setPathSelectedAltIndex(idx)}
                        style={{
                          padding: '10px 12px',
                          fontSize: '12px',
                          fontWeight: 800,
                          background: idx === pathSelectedAltIndex ? 'rgba(167, 139, 250, 0.25)' : 'rgba(255, 255, 255, 0.06)',
                          border: '1px solid rgba(255, 255, 255, 0.12)',
                          borderRadius: '10px',
                          color: '#ffffff',
                          cursor: 'pointer'
                        }}
                        title={JSON.stringify(alt, null, 2)}
                      >
                        #{idx + 1} • est cost: {sourceLabel}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.6 }}>
                  Tip: If no routes are found, adjust SendMax upward (within your risk tolerance) or choose different asset pairs.
                </div>
              </div>
            )}

            {pathPreflight && (
              <div style={{
                marginTop: '14px',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(168, 85, 247, 0.18)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Preflight Summary
                </div>
                <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: 1.7 }}>
                  <div><strong style={{ color: '#a78bfa' }}>Destination exists:</strong> {pathPreflight.destExists ? 'YES' : 'NO (needs XRP activation)'}</div>
                  <div><strong style={{ color: '#a78bfa' }}>Destination requires tag:</strong> {pathPreflight.destRequiresTag ? 'YES' : 'NO'}</div>
                  <div><strong style={{ color: '#a78bfa' }}>Destination DepositAuth:</strong> {pathPreflight.destDepositAuth ? 'YES (incoming restricted)' : 'NO'}</div>
                  <div><strong style={{ color: '#a78bfa' }}>Sender trust line (SendMax):</strong> {pathPreflight.senderHasSendLine ? 'YES' : 'NO'}</div>
                  <div><strong style={{ color: '#a78bfa' }}>Destination trust line (Deliver):</strong> {pathPreflight.destHasDeliverLine ? 'YES' : 'NO'}</div>
                </div>
              </div>
            )}

            {pathReceipt && (
              <div style={{
                marginTop: '14px',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(168, 85, 247, 0.18)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Latest Path Payment Receipt
                </div>
                <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: 1.7 }}>
                  <div><strong style={{ color: '#a78bfa' }}>Hash:</strong> <span style={{ fontFamily: 'monospace' }}>{pathReceipt.hash}</span></div>
                  <div><strong style={{ color: '#a78bfa' }}>Result:</strong> {pathReceipt.transactionResult || 'submitted'}</div>
                  <div><strong style={{ color: '#a78bfa' }}>Validated:</strong> {pathReceipt.validated ? 'YES' : 'NO/UNKNOWN'}</div>
                  {pathReceipt.deliveredAmount && (
                    <div><strong style={{ color: '#a78bfa' }}>Delivered:</strong> {formatXrplAmount(pathReceipt.deliveredAmount)}</div>
                  )}
                  <XummLink
                    href={`https://livenet.xrpl.org/transactions/${pathReceipt.hash}`}
                    style={{ color: '#ddd6fe', textDecoration: 'none', fontSize: '13px', display: 'inline-block', marginTop: '6px' }}
                  >
                    → View on XRPL Explorer
                  </XummLink>
                </div>
              </div>
            )}
          </div>

          {/* Live Price Header */}
          <div style={{
            display: 'flex',
            gap: '24px',
            marginBottom: '32px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              padding: '20px 32px',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '12px',
              flex: 1,
              minWidth: '200px'
            }}>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>LIVE XRP PRICE</div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#10b981' }}>
                ${xrpPrice > 0 ? xrpPrice.toFixed(4) : '-.----'}
              </div>
              <div style={{ fontSize: '12px', color: '#6ee7b7', marginTop: '4px' }}>
                ⬤ LIVE • Updates every 30s
              </div>
            </div>
            <div style={{
              padding: '20px 32px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '12px',
              flex: 1,
              minWidth: '200px'
            }}>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>RLUSD (RIPPLE USD)</div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#60a5fa' }}>
                ${rlusdPrice.toFixed(2)}
              </div>
              <div style={{ fontSize: '12px', color: '#93c5fd', marginTop: '4px' }}>
                ⬤ STABLE • Pegged to USD
              </div>
            </div>
            <div style={{
              padding: '20px 32px',
              background: 'rgba(168, 85, 247, 0.1)',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              borderRadius: '12px',
              flex: 1,
              minWidth: '200px'
            }}>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>XRPL NETWORK</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#a78bfa', marginTop: '8px' }}>
                MAINNET
              </div>
              <div style={{ fontSize: '12px', color: '#c4b5fd', marginTop: '4px' }}>
                ⬤ OPERATIONAL
              </div>
            </div>
          </div>

          {/* Live Price Chart */}
          <div style={{
            padding: '32px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: '600', color: '#e5e7eb' }}>XRP/USD Price Chart</div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>Real-time market activity • Last 10 minutes</div>
              </div>
              {isLoadingDex && (
                <div style={{ fontSize: '13px', color: '#60a5fa' }}>
                  ⟳ Refreshing...
                </div>
              )}
            </div>
            
            {priceHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={priceHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(75, 85, 99, 0.3)" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#9ca3af" 
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#9ca3af" 
                    style={{ fontSize: '12px' }}
                    domain={['dataMin - 0.1', 'dataMax + 0.1']}
                    tickFormatter={(value) => `$${value.toFixed(2)}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#1f2937', 
                      border: '1px solid #60a5fa',
                      borderRadius: '8px',
                      color: '#e5e7eb'
                    }}
                    formatter={(value: number | string | undefined) => {
                      const n = typeof value === 'number' ? value : Number(value);
                      const safe = Number.isFinite(n) ? n : 0;
                      return [`$${safe.toFixed(4)}`, 'Price'];
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="price" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    dot={{ fill: '#10b981', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{
                height: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280'
              }}>
                Loading price history...
              </div>
            )}
          </div>

          {/* Trading Interface & Order Book */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '32px' }}>
            
            {/* Left: Trading Interface */}
            <div style={{
              padding: '32px',
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>Place Order</div>
              
              <select style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                color: '#e5e7eb',
                marginBottom: '24px',
                fontSize: '15px'
              }}>
                <option>XRP / USD</option>
                <option>XRP / USDT</option>
                <option>XRP / RLUSD (Ripple USD)</option>
                <option>RLUSD / USDT</option>
              </select>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                <button style={{
                  flex: 1,
                  padding: '14px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  borderRadius: '8px',
                  color: '#10b981',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}>BUY</button>
                <button style={{
                  flex: 1,
                  padding: '14px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '8px',
                  color: '#ef4444',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}>SELL</button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Amount</div>
                <input
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Price</div>
                <input
                  placeholder={xrpPrice > 0 ? xrpPrice.toFixed(4) : "0.00"}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '16px',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <button style={{
                width: '100%',
                padding: '16px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                color: '#000000',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
                PLACE ORDER
              </button>

              <div style={{
                marginTop: '24px',
                padding: '16px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>SETTLEMENT INFO</div>
                <div style={{ fontSize: '13px', color: '#e5e7eb', lineHeight: '1.6' }}>
                  Orders settle instantly via XRPL consensus. No intermediaries. Full cryptographic verification.
                </div>
              </div>
            </div>

            {/* Right: Order Book */}
            <div style={{
              padding: '32px',
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>
                Order Book
                {isLoadingDex && <span style={{ fontSize: '13px', color: '#60a5fa', marginLeft: '12px' }}>⟳</span>}
              </div>

              {/* Column Headers */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(75, 85, 99, 0.3)'
              }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Price (USD)
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                  Amount (XRP)
                </div>
              </div>

              {/* Asks (Sell Orders) */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', marginBottom: '12px' }}>
                  ASKS (SELL)
                </div>
                {orderBook.asks.length > 0 ? (
                  orderBook.asks.slice(0, 5).reverse().map((ask, i) => (
                    <div key={i} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      padding: '8px 0',
                      fontSize: '14px'
                    }}>
                      <div style={{ color: '#ef4444', fontFamily: 'monospace' }}>
                        ${ask.price}
                      </div>
                      <div style={{ color: '#e5e7eb', textAlign: 'right', fontFamily: 'monospace' }}>
                        {parseFloat(ask.amount).toLocaleString()}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#6b7280', fontSize: '13px', padding: '8px 0' }}>Loading...</div>
                )}
              </div>

              {/* Current Price Divider */}
              <div style={{
                padding: '16px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>CURRENT PRICE</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#60a5fa', fontFamily: 'monospace' }}>
                  ${xrpPrice > 0 ? xrpPrice.toFixed(4) : '-.----'}
                </div>
              </div>

              {/* Bids (Buy Orders) */}
              <div>
                <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '600', marginBottom: '12px' }}>
                  BIDS (BUY)
                </div>
                {orderBook.bids.length > 0 ? (
                  orderBook.bids.slice(0, 5).map((bid, i) => (
                    <div key={i} style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      padding: '8px 0',
                      fontSize: '14px'
                    }}>
                      <div style={{ color: '#10b981', fontFamily: 'monospace' }}>
                        ${bid.price}
                      </div>
                      <div style={{ color: '#e5e7eb', textAlign: 'right', fontFamily: 'monospace' }}>
                        {parseFloat(bid.amount).toLocaleString()}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#6b7280', fontSize: '13px', padding: '8px 0' }}>Loading...</div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Trades */}
          <div style={{
            marginTop: '32px',
            padding: '32px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>Recent Trades</div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '1px solid rgba(75, 85, 99, 0.3)'
            }}>
              <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase' }}>Time</div>
              <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase' }}>Price</div>
              <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase' }}>Amount</div>
              <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase' }}>Type</div>
            </div>

            {recentTrades.map((trade, i) => (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                padding: '12px 0',
                fontSize: '14px',
                borderBottom: i < recentTrades.length - 1 ? '1px solid rgba(75, 85, 99, 0.1)' : 'none'
              }}>
                <div style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '13px' }}>{trade.time}</div>
                <div style={{ color: '#e5e7eb', fontFamily: 'monospace' }}>${trade.price}</div>
                <div style={{ color: '#e5e7eb', fontFamily: 'monospace' }}>{trade.amount}</div>
                <div style={{ 
                  color: trade.type === 'buy' ? '#10b981' : '#ef4444',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  fontSize: '13px'
                }}>
                  {trade.type}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    );
  }

  if (page === 'arbitrage') {
    return <ArbitrageDashboard />;
  }

  if (page === 'stellar') {
    return <StellarDEXDashboard />;
  }

  if (page === 'ecosystem') {
    return <MoneyFlowDashboard />;
  }

  if (page === 'amm') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px'
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '16px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Liquidity (AMM)
          </div>

          <div style={{
            fontSize: '16px',
            color: '#9ca3af',
            marginBottom: '48px'
          }}>
            Automated market maker pools for asset liquidity.
          </div>

          <div style={{
            padding: isMobile ? '28px' : '48px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
            marginBottom: '48px'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '32px' }}>Create New Pool</div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Asset 1</div>
                <select style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '8px',
                  color: '#e5e7eb',
                  fontSize: '15px'
                }}>
                  <option>XRP</option>
                  <option>USDT</option>
                </select>
              </div>

              <div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Asset 2</div>
                <select style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '8px',
                  color: '#e5e7eb',
                  fontSize: '15px'
                }}>
                  <option>USD</option>
                  <option>USDT</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <input placeholder="Amount Asset 1" style={{
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                color: '#e5e7eb',
                fontSize: '15px'
              }} />

              <input placeholder="Amount Asset 2" style={{
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                color: '#e5e7eb',
                fontSize: '15px'
              }} />
            </div>

            <div style={{
              padding: '16px',
              background: 'rgba(59, 130, 246, 0.1)',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#9ca3af' }}>Trading Fee (varies by pool)</span>
                <span style={{ fontSize: '13px', color: '#60a5fa', fontWeight: '600' }}>Varies</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '13px', color: '#9ca3af' }}>Est. Annual Performance</span>
                <span style={{ fontSize: '13px', color: '#9ca3af', fontWeight: '600' }}>Variable</span>
              </div>
            </div>

            <button style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.6) 0%, rgba(30, 64, 175, 0.5) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              color: '#e5e7eb',
              fontWeight: '600',
              fontSize: '15px'
            }}>CREATE POOL</button>
          </div>

          <div style={{ fontSize: '24px', fontWeight: '600', marginBottom: '24px' }}>Active Pools</div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '24px' }}>
            {[
              { pair: 'XRP / USD', tvl: '—', apr: 'Varies' },
              { pair: 'XRP / USDT', tvl: '—', apr: 'Varies' }
            ].map((pool, i) => (
              <div key={i} style={{
                padding: '24px',
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.4) 0%, rgba(15, 23, 42, 0.2) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.1)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>{pool.pair}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>TVL</div>
                    <div style={{ fontSize: '15px', fontWeight: '600' }}>{pool.tvl}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>APR</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#9ca3af' }}>{pool.apr}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (page === 'member-benefits') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />

        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px'
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '16px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Member Benefits
          </div>

          <div style={{
            fontSize: '16px',
            color: '#9ca3af',
            marginBottom: '48px'
          }}>
            Weight-referenced asset verification. Dealer attestation services. Cryptographic settlement infrastructure.
          </div>

          {/* POF-as-a-Service Pricing */}
          <div style={{
            padding: isMobile ? '28px' : '48px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{ fontSize: '28px', fontWeight: '600', marginBottom: '16px', color: '#60a5fa' }}>
              POF-as-a-Service
            </div>
            <div style={{ fontSize: '15px', color: '#9ca3af', marginBottom: '32px', lineHeight: 1.6 }}>
              Real-time cryptographic proof of funds verification for institutional clients and high-value transactions.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
              <div style={{
                padding: '24px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(59, 130, 246, 0.15)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Per-Transaction</div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#60a5fa', marginBottom: '8px' }}>
                  Custom quote
                </div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
                  On-demand verification
                </div>
                <ul style={{ fontSize: '14px', color: '#e5e7eb', lineHeight: 1.8, paddingLeft: '20px', margin: 0 }}>
                  <li>Expedited verification</li>
                  <li>Cryptographic signatures</li>
                  <li>XRPL blockchain proof</li>
                  <li>Documentation package (as configured)</li>
                </ul>
              </div>

              <div style={{
                padding: '24px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(15, 23, 42, 0.4) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px'
              }}>
                <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#60a5fa' }}>
                  Monthly Subscription
                </div>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#60a5fa', marginBottom: '8px' }}>
                  Contact
                </div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
                  Higher-volume plans
                </div>
                <ul style={{ fontSize: '14px', color: '#e5e7eb', lineHeight: 1.8, paddingLeft: '20px', margin: 0 }}>
                  <li>All per-transaction features</li>
                  <li>Higher-volume requests</li>
                  <li>Priority support</li>
                  <li>Optional API access</li>
                  <li>Custom branding</li>
                </ul>
              </div>
            </div>

            <div style={{
              padding: '20px',
              background: 'rgba(59, 130, 246, 0.08)',
              borderRadius: '12px',
              borderLeft: '4px solid #60a5fa'
            }}>
              <div style={{ fontSize: '13px', color: '#93c5fd', fontWeight: '600', marginBottom: '8px' }}>
                💼 INSTITUTIONAL FEATURES
              </div>
              <div style={{ fontSize: '14px', color: '#e5e7eb', lineHeight: 1.6 }}>
                White-label options, dedicated support channels, custom integration assistance, and SLA options available for enterprise clients.
              </div>
            </div>
          </div>

          <div style={{
            padding: '20px',
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '12px',
            fontSize: '14px',
            color: '#fbbf24',
            lineHeight: '1.6',
            marginBottom: '48px'
          }}>
            <strong>Asset Model:</strong> All assets are weight-referenced only. Pricing shown is for verification services, not asset valuation. No price, yield, or redemption guarantees apply to underlying assets.
          </div>

          {/* Additional Member Benefits */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '24px', marginBottom: '48px' }}>
            <div style={{
              padding: '24px',
              background: 'rgba(15, 23, 42, 0.4)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚡</div>
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Instant Settlement</div>
              <div style={{ fontSize: '14px', color: '#9ca3af', lineHeight: 1.6 }}>
                Sub-5-second XRPL transactions with cryptographic finality.
              </div>
            </div>

            <div style={{
              padding: '24px',
              background: 'rgba(15, 23, 42, 0.4)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Secure Custody</div>
              <div style={{ fontSize: '14px', color: '#9ca3af', lineHeight: 1.6 }}>
                Multi-signature wallets with hardware security and compliance.
              </div>
            </div>

            <div style={{
              padding: '24px',
              background: 'rgba(15, 23, 42, 0.4)',
              border: '1px solid rgba(59, 130, 246, 0.15)',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Real-Time Analytics</div>
              <div style={{ fontSize: '14px', color: '#9ca3af', lineHeight: 1.6 }}>
                Live dashboards, transaction monitoring, and audit trails.
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div style={{
            padding: isMobile ? '32px 24px' : '48px',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.25)',
            borderRadius: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '600', marginBottom: '16px' }}>
              Ready to Get Started?
            </div>
            <div style={{ fontSize: '15px', color: '#9ca3af', marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px' }}>
              Contact us to discuss institutional requirements, custom integrations, or to schedule a technical demo.
            </div>
            <button
              onClick={() => setPage('contact')}
              style={{
                padding: '16px 48px',
                fontSize: '16px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.4)';
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (page === 'tokens') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />

        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px'
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '16px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Asset Issuance
          </div>

          <div style={{
            fontSize: '16px',
            color: '#9ca3af',
            marginBottom: '48px'
          }}>
            Create & manage XRPL issued assets (IOUs).
          </div>

          <div style={{
            padding: isMobile ? '28px' : '48px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '32px' }}>Issue New Token</div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Token Name</div>
              <input placeholder="e.g., Property Token" style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                color: '#e5e7eb',
                fontSize: '15px'
              }} />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Token Symbol (3 chars)</div>
              <input placeholder="e.g., RWA" maxLength={3} style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                color: '#e5e7eb',
                fontSize: '15px'
              }} />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Total Supply</div>
              <input placeholder="1000000" style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                color: '#e5e7eb',
                fontSize: '15px'
              }} />
            </div>

            <div style={{ marginBottom: '32px' }}>
              <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '8px' }}>Recipient Wallet</div>
              <input placeholder="r..." style={{
                width: '100%',
                padding: '14px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '8px',
                color: '#e5e7eb',
                fontSize: '15px',
                fontFamily: 'monospace'
              }} />
            </div>

            <div style={{
              padding: '20px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>Service Includes:</div>
              <div style={{ fontSize: '14px', color: '#9ca3af', lineHeight: '1.8' }}>
                • Issuer wallet creation
                <br/>
                • Trust line setup
                <br/>
                • Token issuance
                <br/>
                • Transaction verification
              </div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#60a5fa', marginTop: '16px' }}>
                Service Fee: 500 XRP
              </div>
            </div>

            <button style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.6) 0%, rgba(30, 64, 175, 0.5) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '8px',
              color: '#e5e7eb',
              fontWeight: '600',
              fontSize: '15px'
            }}>REQUEST TOKEN ISSUANCE</button>
          </div>
        </div>
      </div>
    );
  }

  if (page === 'terms') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />
        <div style={{ padding: isMobile ? '80px 0 40px' : '100px 0 60px' }}>
          <Terms />
        </div>
        <Footer />
      </div>
    );
  }

  if (page === 'privacy') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />
        <div style={{ padding: isMobile ? '80px 0 40px' : '100px 0 60px' }}>
          <Privacy />
        </div>
        <Footer />
      </div>
    );
  }

  if (page === 'legal') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />

        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px',
          flex: 1
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '48px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Legal & Terms
          </div>

          <div style={{ marginBottom: '48px' }}>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#ffffff', marginBottom: '16px' }}>
              Terms of Service
            </div>
            <div style={{ fontSize: '15px', color: '#d1d5db', lineHeight: '1.8', marginBottom: '24px' }}>
              By accessing UNYKORN infrastructure, you agree to the following terms:
            </div>
            <div style={{ fontSize: '14px', color: '#9ca3af', lineHeight: '1.8', paddingLeft: '20px' }}>
              1. <strong style={{ color: '#e5e7eb' }}>Service Scope</strong>: UNYKORN provides verification, documentation, and settlement infrastructure for XRPL issued assets. We do not provide custody, redemption guarantees, price guarantees, or investment advice.
              <br/><br/>
              2. <strong style={{ color: '#e5e7eb' }}>Payment Terms</strong>: All fees are payable in XRP or XRPL-issued assets to the designated wallet address. Services activate upon confirmed payment.
              <br/><br/>
              3. <strong style={{ color: '#e5e7eb' }}>Wallet Security</strong>: You are solely responsible for securing any wallet seeds or private keys generated through our platform. UNYKORN cannot recover lost seeds.
              <br/><br/>
              4. <strong style={{ color: '#e5e7eb' }}>No Investment Advice</strong>: UNYKORN is not a registered investment advisor. Nothing on this platform constitutes investment, financial, legal, or tax advice.
              <br/><br/>
              5. <strong style={{ color: '#e5e7eb' }}>Regulatory Compliance</strong>: You are responsible for compliance with all applicable laws in your jurisdiction regarding digital assets.
            </div>
          </div>

          <div style={{ marginBottom: '48px' }}>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#ffffff', marginBottom: '16px' }}>
              Risk Disclosure
            </div>
            <div style={{
              padding: '24px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              fontSize: '14px',
              color: '#fca5a5',
              lineHeight: '1.8'
            }}>
              <strong style={{ color: '#ef4444' }}>IMPORTANT RISKS:</strong><br/><br/>
              • Digital assets are highly volatile and may lose value
              <br/>
              • XRPL issued assets (IOUs) are credit obligations, not insured deposits
              <br/>
              • Trust lines represent credit exposure to the issuer
              <br/>
              • Blockchain transactions are irreversible
              <br/>
              • No FDIC insurance or government guarantees apply
              <br/>
              • Past performance does not guarantee future results
            </div>
          </div>

          <div style={{ marginBottom: '48px' }}>
            <div style={{ fontSize: '24px', fontWeight: '600', color: '#ffffff', marginBottom: '16px' }}>
              Privacy Policy
            </div>
            <div style={{ fontSize: '14px', color: '#9ca3af', lineHeight: '1.8' }}>
              UNYKORN collects minimal information necessary to provide services. We log XRPL wallet addresses for verification purposes. We do not sell user data. All blockchain transactions are public by nature of the XRP Ledger.
            </div>
          </div>

          <div style={{
            padding: '24px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '12px',
            fontSize: '13px',
            color: '#9ca3af',
            textAlign: 'center'
          }}>
            Last Updated: January 3, 2026<br/>
            Questions? <button onClick={() => setPage('contact')} style={{ background: 'none', border: 'none', color: '#60a5fa', textDecoration: 'underline', cursor: 'pointer' }}>Contact Us</button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  if (page === 'faq') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />
        <FloatingHomeButton />

        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px',
          flex: 1
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '48px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Frequently Asked Questions
          </div>

          {[
            {
              q: 'What is UNYKORN?',
              a: 'UNYKORN is institutional infrastructure for verification, documentation, and settlement workflows on XRPL issued assets. We provide proof-of-funds packages, trust line verification, and settlement tooling for institutions.'
            },
            {
              q: 'How does POF-as-a-Service work?',
              a: 'You mint a wallet through our platform, define the verification scope, and complete onboarding. We help configure trust lines and produce a documentation package that references public-ledger data. Availability and terms vary by counterparty and use case.'
            },
            {
              q: 'Is this the same as a bank account?',
              a: 'No. XRPL issued assets (IOUs) are credit obligations, not deposits. They are not FDIC insured and carry counterparty risk. This is infrastructure for proof of capacity, not custody.'
            },
            {
              q: 'Who holds the private keys?',
              a: 'For POF-as-a-Service, YOU hold the private keys to the generated wallet. We never have access to your seed. For verification services, we provide public account references and documentation for institutional due diligence.'
            },
            {
              q: 'Can I redeem USDT for USD?',
              a: 'UNYKORN does not provide redemption services. XRPL issued assets are issuer obligations (IOUs) and carry counterparty risk. Redemption, if any, depends on the issuer\'s terms.'
            },
            {
              q: 'What are trust lines?',
              a: 'Trust lines are XRPL\'s mechanism for holding issued assets. When you establish a trust line to an issuer, you\'re declaring willingness to hold that issuer\'s IOUs. This is how balances appear on your wallet.'
            },
            {
              q: 'How do I verify balances on-ledger?',
              a: 'Use public explorers (e.g., livenet.xrpl.org or xrpscan.com) to inspect referenced accounts, trust lines, and transactions. Balances change over time; always verify at the time you need evidence.'
            },
            {
              q: 'What if I lose my wallet seed?',
              a: 'UNYKORN cannot recover lost seeds. You are solely responsible for securing your private keys. We recommend secure offline storage and never sharing your seed.'
            },
            {
              q: 'How long does verification take?',
              a: 'Timelines vary by request scope and onboarding requirements. Most requests are handled within 1–9 business days after payment confirmation.'
            },
            {
              q: 'Can I use this for legal proceedings?',
              a: 'Our documentation is designed to be attorney-ready and includes cryptographic references to the public ledger. However, acceptance depends on the jurisdiction and specific legal context. Consult your attorney.'
            }
          ].map((item, i) => (
            <div key={i} style={{
              padding: isMobile ? '24px' : '32px',
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                {item.q}
              </div>
              <div style={{ fontSize: '15px', color: '#d1d5db', lineHeight: '1.7' }}>
                {item.a}
              </div>
            </div>
          ))}

          <div style={{
            marginTop: '48px',
            textAlign: 'center',
            fontSize: '15px',
            color: '#9ca3af'
          }}>
            Still have questions? <button onClick={() => setPage('contact')} style={{ background: 'none', border: 'none', color: '#60a5fa', textDecoration: 'underline', cursor: 'pointer', fontSize: '15px' }}>Contact our team</button>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  if (page === 'contact') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />
        <FloatingHomeButton />

        <div style={{
          maxWidth: '700px',
          margin: '0 auto',
          padding: isMobile ? '100px 20px 60px' : '140px 48px 80px',
          flex: 1
        }}>
          <div style={{
            fontSize: isMobile ? '32px' : '48px',
            fontWeight: '300',
            marginBottom: '16px',
            background: 'linear-gradient(180deg, #ffffff 0%, #e5e7eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Contact & Support
          </div>

          <div style={{
            fontSize: '16px',
            color: '#9ca3af',
            marginBottom: '48px'
          }}>
            Enterprise inquiries and technical support.
          </div>

          <div style={{
            padding: isMobile ? '28px' : '40px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>
              Payment Address
            </div>
            <div style={{
              fontSize: '14px',
              fontFamily: 'monospace',
              color: '#60a5fa',
              padding: '16px',
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '8px',
              wordBreak: 'break-all',
              marginBottom: '16px'
            }}>
              rpP12ND2K7ZRzXZBEUnQM2i18tMGytXnW1
            </div>
            <div style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.6' }}>
              Send XRP payments to this address with a memo describing your service request. Include your contact wallet address for responses.
            </div>
          </div>

          <div style={{
            padding: isMobile ? '28px' : '40px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(15, 23, 42, 0.3) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
            marginBottom: '32px'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '600', marginBottom: '24px' }}>
              Enterprise Inquiries
            </div>
            <div style={{ fontSize: '15px', color: '#d1d5db', lineHeight: '1.8', marginBottom: '24px' }}>
              For institutional partnerships, custom verification packages, or high-volume settlement needs:
            </div>
            <div style={{ fontSize: '14px', color: '#9ca3af', lineHeight: '1.6' }}>
              • Banks & Lenders: Bulk verification packages available
              <br/>
              • Insurance Underwriters: Custom audit reports
              <br/>
              • Legal Firms: Attorney consultation packages
              <br/>
              • OTC Desks: Settlement infrastructure integration
            </div>
          </div>

          <div style={{
            padding: '24px',
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '12px',
            fontSize: '14px',
            color: '#fbbf24',
            lineHeight: '1.6'
          }}>
            <strong>Response Time:</strong> We aim to respond within 1–2 business days. Support requests are processed in order of onboarding and payment confirmation.
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  if (page === 'xaman-services') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column'
      }}>
        <NavBar />
        <FloatingHomeButton />
        <div style={{ paddingTop: isMobile ? '80px' : '100px' }}>
          <XamanServicesHub />
        </div>
        <Footer />
      </div>
    );
  }

  if (page === 'wallets') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />
        <FloatingHomeButton />
        <div style={{ padding: isMobile ? '100px 20px 60px' : '140px 48px 80px' }}>
          <div style={{ fontSize: isMobile ? '32px' : '48px', fontWeight: '300', marginBottom: '32px' }}>
            Wallet Registry
          </div>
          <div style={{ color: '#9ca3af' }}>Coming soon...</div>
        </div>
      </div>
    );
  }

  if (page === 'portfolio') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#000000',
        color: '#e5e7eb',
        fontFamily: "'SF Pro Display', system-ui, sans-serif"
      }}>
        <NavBar />
        <FloatingHomeButton />
        <div style={{ padding: isMobile ? '100px 20px 60px' : '140px 48px 80px' }}>
          <div style={{ fontSize: isMobile ? '32px' : '48px', fontWeight: '300', marginBottom: '32px' }}>
            Portfolio
          </div>
          <div style={{ color: '#9ca3af' }}>Coming soon...</div>
        </div>
      </div>
    );
  }

  return null;
};

export default UnykornLanding;
